// Reusable primitives

import { animate as __motionAnimate } from 'motion/mini';

// ── Motion helpers ───────────────────────────────────────────────────────
// One entry point for all enter/exit animation in the app so the motion
// language stays uniform: short (≤180ms), opacity plus a few px of travel,
// standard ease. Durations collapse to 0 under prefers-reduced-motion, so
// mount/unmount sequencing still flows through the same promise chain.
const MOTION = { fast: 0.14, base: 0.18, ease: [0.25, 0.1, 0.25, 1] };
const prefersReducedMotion = () => {
  try { return window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch (_) { return false; }
};
// Returns a promise that resolves when the animation settles. Callers gate
// unmounts on this, so it must ALWAYS settle: the controls object motion
// returns is awaited as a thenable (its `.finished` does not resolve while
// the final keyframe is persisted), and a timer at duration+80ms backstops
// interruption/cancel paths.
const animateEl = (el, keyframes, opts = {}) => {
  if (!el) return Promise.resolve();
  const duration = prefersReducedMotion() ? 0 : (opts.duration ?? MOTION.fast);
  return new Promise((resolve) => {
    setTimeout(resolve, duration * 1000 + 80);
    try {
      Promise.resolve(__motionAnimate(el, keyframes, { ease: MOTION.ease, ...opts, duration }))
        .then(resolve, resolve);
    } catch (_) { resolve(); }
  });
};

// Device snapshot (src/device.jsx). Read at render — the kind only changes on
// a resize across a breakpoint, and the shell re-renders on that.
const dev = () => window.AtlasDevice || { phone: false, tablet: false, desktop: true, touch: false, canHover: true };

// ── Accessibility hooks ──────────────────────────────────────────────────
// Keep Tab focus inside `containerRef` while `active`. Restores focus to
// the previously-focused element on close, so closing a Drawer/Dialog
// returns the user to whatever triggered it.
const useFocusTrap = (active, containerRef) => {
  React.useEffect(() => {
    if (!active || !containerRef.current) return;
    const container = containerRef.current;
    const SELECTOR = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
    const getFocusable = () => Array.from(container.querySelectorAll(SELECTOR))
      .filter((el) => el.offsetParent !== null || el === document.activeElement);
    const prevFocus = document.activeElement;
    // Focus the first focusable on mount, unless something inside is already
    // focused. Not on touch: that pops the on-screen keyboard over a sheet the
    // user only meant to look at (and iOS scrolls the page to the field).
    if (!dev().touch && !container.contains(document.activeElement)) {
      const first = getFocusable()[0];
      if (first) first.focus();
    }
    const onKey = (e) => {
      if (e.key !== 'Tab') return;
      const f = getFocusable();
      if (!f.length) { e.preventDefault(); return; }
      const first = f[0];
      const last  = f[f.length - 1];
      const at    = document.activeElement;
      if (e.shiftKey) {
        if (at === first || !container.contains(at)) { e.preventDefault(); last.focus(); }
      } else {
        if (at === last  || !container.contains(at)) { e.preventDefault(); first.focus(); }
      }
    };
    container.addEventListener('keydown', onKey);
    return () => {
      container.removeEventListener('keydown', onKey);
      if (prevFocus && typeof prevFocus.focus === 'function') {
        try { prevFocus.focus(); } catch (_) {}
      }
    };
  }, [active]);
};

// Fire `onEscape` when Escape is pressed while `active`. Listens at the
// document level so it works regardless of which focus-trapped element
// currently has focus.
// Escape handling is stack-aware so nested overlays (e.g. "+ New company"
// drawer opened from inside the New person drawer) close one layer at a
// time — only the most-recently-opened active layer reacts to Esc.
// `opts.layout` (FullScreenPanel) registers before the passive effects of its children.
const _escStack = [];
const useEscapeKey = (active, onEscape, opts) => {
  // Ref keeps the latest callback without re-registering (inline arrows
  // change every render; re-pushing would scramble the stack order).
  const cbRef = React.useRef(onEscape);
  cbRef.current = onEscape;
  // opts.layout: register in the layout phase, so an overlay shown in the same
  // commit as overlays nested inside it (a workspace re-shown with a drawer
  // open) sits below them in the stack. Constant per call site.
  const useFx = opts && opts.layout ? React.useLayoutEffect : React.useEffect;
  useFx(() => {
    if (!active) return;
    const entry = { fire: () => cbRef.current?.() };
    _escStack.push(entry);
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      if (_escStack[_escStack.length - 1] !== entry) return;
      e.preventDefault();
      entry.fire();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      const i = _escStack.indexOf(entry);
      if (i >= 0) _escStack.splice(i, 1);
      document.removeEventListener('keydown', onKey);
    };
  }, [active]);
};

// ── Layers ───────────────────────────────────────────────────────────────
// FullScreenPanel (views/record_panels.jsx) provides { portalTarget, zBase: 55,
// hidden }. Drawer, NoteModal and Combobox read it: anything opened inside a
// workspace stacks above it (z = max(own, zBase + 15), record_panels_core
// layerZ), and goes quiet while the workspace is hidden (no Escape/focus
// entry, no visible body-portaled sheet or menu).
const LayerContext = React.createContext({ portalTarget: null, zBase: 0, hidden: false });
window.LayerContext = LayerContext;
// The rule lives in core only (entry.jsx loads it first); without it, the own z.
const _layerZ = (own, zBase) => {
  const core = window.RecordPanelsCore;
  return core && core.layerZ ? core.layerZ(own, zBase) : own;
};

// `maxW` caps the pill's width and ellipsises the label instead of letting a
// long one (e.g. a verbatim DealCloud stage) overflow its table cell.
const Pill = ({ hue = 'slate', children, dot = false, sz = 'md', maxW, title }) => {
  const h = HUE[hue] || HUE.slate;
  const padding = sz === 'sm' ? '1px 6px' : '2px 8px';
  return (
    <span title={title} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: h.bg, color: h.fg, padding, borderRadius: 999, fontSize: sz === 'sm' ? 11 : 11.5, fontWeight: 500, lineHeight: 1.4, whiteSpace: 'nowrap', maxWidth: maxW, overflow: maxW ? 'hidden' : undefined }}>
      {dot && <span style={{ width: 6, height: 6, borderRadius: 999, background: h.dot, flexShrink: 0 }} />}
      {maxW
        ? <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}>{children}</span>
        : children}
    </span>
  );
};

// ── Hot score (202609161000) ──────────────────────────────────────────────
// The team's MANUAL 1–10 priority on a deal — set while cleaning up the
// book so "which of my hot leads matter most" is a sort, not a memory.
// Read view: the number in a pill, hue by band (1–3 cool, 4–6 warm, 7–10
// hot), or a dim "—" when unscored. Click → a ten-step picker (the
// "slider"): hover previews the score, click sets it, clicking the current
// score again clears it, ←/→ nudge, a digit sets it (0 = 10), Enter keeps,
// Escape cancels. The picker is portaled to <body> with fixed positioning
// (the Combobox menu's trick) so the grid's overflow can't clip it at
// either scroll edge; it is clamped inside the viewport and closes on any
// scroll rather than drifting away from its cell.
const hotScoreHue = (n) => (n == null ? 'slate' : n >= 7 ? 'rose' : n >= 4 ? 'amber' : 'slate');
const HOT_SCORE_STEPS = Array.from({ length: 10 }, (_, i) => i + 1);
const HOT_SCORE_PICKER_W = 236;

const HotScoreBadge = ({ value, sz = 'sm' }) => (
  <Pill hue={hotScoreHue(value)} dot sz={sz} title={`Hot score ${value} of 10`}>
    <span className="num" style={{ fontWeight: 600 }}>{value}</span>
  </Pill>
);

const HotScoreCell = ({ value, onSave, editable = true, fill = false, sz = 'sm' }) => {
  const [editing, setEditing] = React.useState(false);
  const [preview, setPreview] = React.useState(null);
  const [saving, setSaving]   = React.useState(false);
  const [hovered, setHovered] = React.useState(false);
  const [pos, setPos]         = React.useState(null);
  const anchorRef = React.useRef(null);
  const boxRef    = React.useRef(null);

  const close = React.useCallback(() => { setEditing(false); setPreview(null); }, []);

  const commit = async (next) => {
    const normalized = next == null ? null : Number(next);
    if (normalized === (value ?? null)) { close(); return; }
    setSaving(true);
    try { await onSave(normalized); }
    catch (_) { /* the caller toasts; the read view keeps the old value */ }
    finally { setSaving(false); close(); }
  };

  // Place the picker over its cell: vertically centered on the cell, its
  // left edge on the cell's left edge, clamped so it never leaves the viewport.
  React.useLayoutEffect(() => {
    if (!editing || !anchorRef.current) return;
    const r = anchorRef.current.getBoundingClientRect();
    const vw = window.innerWidth;
    const left = Math.max(8, Math.min(r.left - 6, vw - HOT_SCORE_PICKER_W - 8));
    setPos({ left, top: r.top + r.height / 2 });
  }, [editing]);

  // Click-away closes without saving; any scroll closes too (the picker is
  // fixed, so it would otherwise stay put while its cell moved).
  React.useEffect(() => {
    if (!editing) return undefined;
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) close(); };
    const onScroll = (e) => { if (!(boxRef.current && boxRef.current.contains(e.target))) close(); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('scroll', onScroll, true);
    };
  }, [editing, close]);
  React.useEffect(() => { if (editing && pos && boxRef.current) boxRef.current.focus(); }, [editing, pos]);

  const shown = preview ?? value ?? null;
  const band = HUE[hotScoreHue(shown)] || HUE.slate;
  const onKey = (e) => {
    if (e.key === 'Escape') { e.stopPropagation(); close(); }
    else if (e.key === 'Enter') { e.stopPropagation(); commit(shown); }
    else if (e.key === 'ArrowRight' || e.key === 'ArrowUp') { e.preventDefault(); setPreview(Math.min(10, (shown ?? 0) + 1)); }
    else if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') { e.preventDefault(); setPreview(Math.max(1, (shown ?? 2) - 1)); }
    else if (/^[0-9]$/.test(e.key)) { e.preventDefault(); commit(e.key === '0' ? 10 : Number(e.key)); }
  };

  return (
    <span
      ref={anchorRef}
      onClick={(e) => { if (!editable) return; e.stopPropagation(); if (!editing) { setHovered(false); setEditing(true); } }}
      onMouseEnter={() => { if (editable && !editing) setHovered(true); }}
      onMouseLeave={() => setHovered(false)}
      title={editable ? 'Click to set the hot score (1–10)' : (value != null ? `Hot score ${value} of 10` : undefined)}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 4, minWidth: 0, maxWidth: '100%',
        ...(fill ? { width: '100%', alignSelf: 'stretch', justifyContent: 'flex-end' } : null),
        cursor: editable ? 'pointer' : 'default',
        padding: '2px 4px', marginLeft: -4, borderRadius: 4,
        background: hovered ? 'var(--surface-hover)' : 'transparent', transition: 'background .1s',
        opacity: editing ? 0.45 : 1,
      }}>
      {value == null
        ? <span style={{ color: 'var(--text-4)', fontSize: 11.5 }}>—</span>
        : <HotScoreBadge value={value} sz={sz} />}
      {editable && hovered && !saving && !editing && (
        <span style={{
          flexShrink: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          width: 18, height: 18, borderRadius: 4, marginLeft: fill ? 'auto' : 0,
          background: 'var(--surface)', border: '1px solid var(--border)',
        }}><Icon.Edit size={11} color="var(--text-3)" /></span>
      )}
      {saving && <span style={{ fontSize: 10, color: 'var(--text-4)', flexShrink: 0 }}>saving…</span>}

      {editing && pos && ReactDOM.createPortal(
        <span ref={boxRef} tabIndex={0} onKeyDown={onKey} role="slider"
          data-combobox-menu=""
          /* ^ outside-click closers (FilterPopover etc.) treat clicks in here as inside */
          aria-label="Hot score" aria-valuemin={1} aria-valuemax={10} aria-valuenow={shown ?? undefined}
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          style={{
            position: 'fixed', left: pos.left, top: pos.top, transform: 'translateY(-50%)', zIndex: 2000,
            display: 'inline-flex', alignItems: 'center', gap: 8, padding: '5px 8px',
            background: 'var(--surface)', border: '1px solid var(--accent)', borderRadius: 6,
            boxShadow: 'var(--shadow-lg)', outline: 'none', whiteSpace: 'nowrap', fontSize: 12,
          }}>
          <span style={{ display: 'inline-flex', gap: 3 }} onMouseLeave={() => setPreview(null)}>
            {HOT_SCORE_STEPS.map((n) => {
              const lit = shown != null && n <= shown;
              return (
                <button key={n} type="button" title={n === value ? `${n} — click again to clear` : `Set ${n}`}
                  onMouseEnter={() => setPreview(n)}
                  onClick={() => commit(n === value ? null : n)}
                  style={{
                    width: 11, height: 18, padding: 0, cursor: 'pointer',
                    border: `1px solid ${lit ? band.dot : 'var(--border)'}`, borderRadius: 3,
                    background: lit ? band.dot : 'var(--surface)', opacity: lit ? 1 : 0.9,
                    transition: 'background .08s',
                  }} />
              );
            })}
          </span>
          <span className="num" style={{ fontWeight: 600, minWidth: 18, textAlign: 'right', color: shown == null ? 'var(--text-4)' : 'var(--text)' }}>
            {shown ?? '—'}
          </span>
          {value != null && (
            <button type="button" onClick={() => commit(null)} title="Clear the score"
              style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--text-3)', fontSize: 11, padding: '0 2px' }}>
              clear
            </button>
          )}
        </span>,
        document.body,
      )}
    </span>
  );
};
window.HotScoreCell  = HotScoreCell;
window.HotScoreBadge = HotScoreBadge;
window.hotScoreHue   = hotScoreHue;

// ── Derived relationship roles (contacts.roles / companies.roles) ─────────
// Facts computed hourly by derive_roles.py — never hand-maintained (the old
// contact_type badge was hardcoded 'seller' for every synced contact).
// Object order = display priority when truncating.
const ROLE_META = {
  seller:           { label: 'Seller',           hue: 'blue',
                      hint: 'Their company has a seller-side engagement.' },
  buyer:            { label: 'Buyer',            hue: 'violet',
                      hint: 'Buyer-side: a buyer firm (or carries PB/PEG buyer tags).' },
  referral_partner: { label: 'Referral partner', hue: 'teal',
                      hint: 'On the Brand Builder list — refers deals to IndustryPro.' },
  gpp:              { label: 'GPP',              hue: 'green',
                      hint: 'Growth Partner Program member.' },
  referred:         { label: 'Referred',         hue: 'amber',
                      hint: 'Came in as an incoming referral.' },
  referral_source:  { label: 'Referral source',  hue: 'amber',
                      hint: 'Has referred other contacts to IndustryPro.' },
};

// Seller tools (seller brief, comps, data vault, MyExit) belong on sellers
// only. `buyer` is the company's buyer-roster row: undefined = not loaded yet
// (false, so nothing mounts and then vanishes), null = not on the roster. A
// page that never loads the roster row passes null — every roster firm also
// carries the derived 'buyer' role (derive_roles.py), so it still reads as a
// buyer — and, like the roster row, outranks a seller-side engagement, so
// both pages answer the same. A seller-side engagement (derived 'seller')
// otherwise makes it a seller whatever the static role says; a prospect with
// no roles at all is a seller.
// `people` (optional) = contacts at the company: companies.roles never carries
// 'gpp' (a contact role), so a GPP partner's firm is read off its people.
// null = not loaded yet (false, like an unknown buyer); omitted = not checked.
const NOT_SELLER_COMPANY_ROLES = new Set(['buyer', 'partner', 'service_provider']);
const REFERRAL_ONLY_ROLES = new Set(['gpp', 'referral_partner', 'referred', 'referral_source']);
const personRoles = (p) => (p && (p.roles || (p._raw && p._raw.roles))) || [];
const isSellerCompany = (company, buyer, people) => {
  if (!company || buyer === undefined || buyer || people === null) return false;
  const raw = company._raw || company;
  const roles = [...(raw.roles || [])];
  if ((people || []).some((p) => personRoles(p).includes('gpp'))) roles.push('gpp');
  if (roles.includes('buyer')) return false;
  if (roles.includes('seller')) return true;
  if (NOT_SELLER_COMPANY_ROLES.has(raw.role)) return false;
  return !(roles.length && roles.every((r) => REFERRAL_ONLY_ROLES.has(r)));
};
window.isSellerCompany = isSellerCompany;

// details = optional {role: string} overrides for the hover text — pages
// with the underlying facts loaded (contact/company detail) pass specifics
// ("Referred Jul 16, 2026 by …"); grids fall back to the generic hint.
const RoleChips = ({ roles, max = 4, sz = 'sm', details }) => {
  const known = Object.keys(ROLE_META).filter((r) => (roles || []).includes(r));
  if (known.length === 0) return null;
  const shown = known.slice(0, max);
  const extra = known.length - shown.length;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
      {shown.map((r) => (
        <span key={r} title={(details && details[r]) || ROLE_META[r].hint} style={{ display: 'inline-flex' }}>
          <Pill hue={ROLE_META[r].hue} sz={sz}>{ROLE_META[r].label}</Pill>
        </span>
      ))}
      {extra > 0 && (
        <span title={known.slice(max).map((r) => ROLE_META[r].label).join(', ')}
              style={{ fontSize: 10.5, color: 'var(--text-4)' }}>+{extra}</span>
      )}
    </span>
  );
};

// Finger-sized on touch: the same three sizes, taller, so all ~550 call
// sites get usable tap targets without touching them. Desktop is unchanged.
const BTN_SIZES = {
  desktop: {
    xs: { padding: '4px 7px', fontSize: 11.5, h: 24, gap: 4 },
    sm: { padding: '5px 9px', fontSize: 12, h: 28, gap: 5 },
    md: { padding: '6px 11px', fontSize: 12.5, h: 30, gap: 6 },
  },
  tablet: {
    xs: { padding: '4px 9px', fontSize: 12, h: 30, gap: 5 },
    sm: { padding: '5px 11px', fontSize: 12.5, h: 34, gap: 6 },
    md: { padding: '6px 13px', fontSize: 13, h: 38, gap: 6 },
  },
  phone: {
    xs: { padding: '4px 10px', fontSize: 13, h: 32, gap: 5 },
    sm: { padding: '5px 12px', fontSize: 13.5, h: 36, gap: 6 },
    md: { padding: '6px 14px', fontSize: 14.5, h: 42, gap: 7 },
  },
};
// Phone tap target (step 15): an invisible box ≥44×44 centred on its host, so a 32px
// button or a 22px chip keeps its look and still takes a finger (a tap on the box is a
// tap on the host). The host needs position: relative (phoneHitHost()). Desktop: nothing.
const PHONE_HIT = { position: 'absolute', left: '50%', top: '50%', width: '100%', height: '100%', minWidth: 44, minHeight: 44, transform: 'translate(-50%, -50%)' };
const phoneHitHost = () => (dev().phone ? { position: 'relative' } : null);
// "Open anyway" (mobile/shell.jsx PhoneRoute) shows a desktop page at desktop width on a
// phone; under PhoneHitScope false its rows keep their desktop geometry (no box).
const PhoneHitScope = React.createContext(true);
const PhoneHit = () => {
  const on = React.useContext(PhoneHitScope);
  return dev().phone && on ? <span aria-hidden data-hit="" style={PHONE_HIT} /> : null;
};
const Btn = ({ variant = 'ghost', size = 'md', icon, children, onClick, active = false, style, title, disabled = false }) => {
  const d = dev();
  const s = (d.phone ? BTN_SIZES.phone : d.touch ? BTN_SIZES.tablet : BTN_SIZES.desktop)[size];
  const hover = d.canHover !== false;
  const variants = {
    primary: { background: 'var(--text)', color: 'var(--bg)', border: '1px solid var(--text)' },
    accent:  { background: 'var(--accent)', color: '#fff', border: '1px solid var(--accent)' },
    outline: { background: 'var(--surface)', color: 'var(--text)', border: '1px solid var(--border)' },
    ghost:   { background: active ? 'var(--surface-hover)' : 'transparent', color: active ? 'var(--text)' : 'var(--text-2)', border: '1px solid transparent' },
  };
  const v = variants[variant];
  return (
    <button
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      title={title}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: s.gap, padding: s.padding, fontSize: s.fontSize, height: s.h,
        borderRadius: 7, fontWeight: 500, cursor: disabled ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap',
        opacity: disabled ? 0.5 : 1, ...(d.phone ? { position: 'relative' } : null),
        transition: 'background .12s, border-color .12s, color .12s',
        ...v, ...style,
      }}
      onMouseEnter={(e) => { if (disabled || !hover) return; if (variant === 'ghost') e.currentTarget.style.background = 'var(--surface-hover)'; if (variant === 'outline') e.currentTarget.style.borderColor = 'var(--border-strong)'; }}
      onMouseLeave={(e) => { if (disabled || !hover) return; if (variant === 'ghost' && !active) e.currentTarget.style.background = 'transparent'; if (variant === 'outline') e.currentTarget.style.borderColor = 'var(--border)'; }}
    >
      {d.phone && <PhoneHit />}
      {icon}
      {children}
    </button>
  );
};

const Kbd = ({ children }) => (
  <kbd style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: 18, height: 18, padding: '0 4px', borderRadius: 4, background: 'var(--surface-2)', border: '1px solid var(--border)', borderBottomWidth: 1.5, color: 'var(--text-2)', fontFamily: 'inherit', fontSize: 10.5, fontWeight: 500 }}>{children}</kbd>
);

const Avatar = ({ name, color = 'var(--accent)', size = 22 }) => {
  const initials = (name || '?').split(' ').map(s => s[0]).slice(0, 2).join('').toUpperCase();
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: size, height: size, borderRadius: 999, background: color, color: '#fff', fontSize: size * 0.42, fontWeight: 600, fontFamily: 'var(--font-display)', flexShrink: 0 }}>
      {initials}
    </span>
  );
};

const Logo = ({ name, color = 'var(--accent)', size = 22 }) => {
  const letter = (name || '?').replace(/[^A-Za-z]/g, '').slice(0, 1).toUpperCase();
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: size, height: size, borderRadius: 6, background: color, color: '#fff', fontSize: size * 0.5, fontWeight: 700, fontFamily: 'var(--font-display)', flexShrink: 0, letterSpacing: '-0.04em' }}>
      {letter}
    </span>
  );
};

const Sparkline = ({ data, color = 'var(--accent)', w = 64, h = 18 }) => {
  const max = Math.max(...data), min = Math.min(...data);
  const range = max - min || 1;
  const pts = data.map((v, i) => `${(i / (data.length - 1)) * w},${h - ((v - min) / range) * (h - 2) - 1}`).join(' ');
  return (
    <svg width={w} height={h} style={{ display: 'block' }}>
      <polyline points={pts} fill="none" stroke={color} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
};

// Health bar
const HealthBar = ({ value }) => {
  const hue = value >= 75 ? 'oklch(0.55 0.10 155)' : value >= 50 ? 'oklch(0.65 0.13 75)' : 'oklch(0.55 0.14 25)';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <div style={{ flex: 1, height: 4, background: 'var(--surface-2)', borderRadius: 999, overflow: 'hidden', minWidth: 36 }}>
        <div style={{ width: `${value}%`, height: '100%', background: hue, borderRadius: 999 }} />
      </div>
      <span className="num" style={{ fontSize: 11.5, color: 'var(--text-2)', fontVariantNumeric: 'tabular-nums' }}>{value}</span>
    </div>
  );
};

// ── Skeleton loaders ─────────────────────────────────────────────────────
// Shimmer placeholders for lists that are still resolving. Cheaper visual
// signal than "Loading…" text — keeps layout stable when data arrives.
// One <style> block is injected once (idempotent) for the keyframes.
const __SKELETON_STYLE_ID = 'atlas-skeleton-keyframes';
if (typeof document !== 'undefined' && !document.getElementById(__SKELETON_STYLE_ID)) {
  const el = document.createElement('style');
  el.id = __SKELETON_STYLE_ID;
  el.textContent = `
    @keyframes atlas-shimmer {
      0%   { background-position: -200px 0; }
      100% { background-position: calc(200px + 100%) 0; }
    }
    .atlas-skel {
      display: inline-block;
      background: linear-gradient(90deg, var(--surface-2) 0%, var(--surface-hover) 50%, var(--surface-2) 100%);
      background-size: 200px 100%;
      background-repeat: no-repeat;
      animation: atlas-shimmer 1.4s ease-in-out infinite;
      border-radius: 4px;
      vertical-align: middle;
    }
  `;
  document.head.appendChild(el);
}

const Skeleton = ({ w = '100%', h = 12, style, className }) => (
  <span
    className={`atlas-skel ${className || ''}`}
    style={{
      width: typeof w === 'number' ? `${w}px` : w,
      height: typeof h === 'number' ? `${h}px` : h,
      ...style,
    }}
  />
);

// Stable per-row width cycle so the skeleton doesn't visually "jump" between
// re-renders the way Math.random() would.
const __SKEL_WIDTHS = [62, 48, 70, 55, 44, 66, 51, 58, 73, 49];

// One ghosted row for a list view. `variant` controls the column layout so
// the skeleton mirrors the real row shape:
//   • engagement — circle avatar + deal name + stage pill + revenue
//   • company    — square logo + name + industry pill + city
//   • generic    — circle + name + meta
const SkeletonRow = ({ variant = 'generic', i = 0 }) => {
  const cell = { display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', borderBottom: '1px solid var(--border)' };
  const wA = __SKEL_WIDTHS[i % __SKEL_WIDTHS.length];
  const wB = __SKEL_WIDTHS[(i + 4) % __SKEL_WIDTHS.length];
  if (variant === 'engagement') {
    return (
      <div style={cell}>
        <Skeleton w={26} h={26} style={{ borderRadius: '50%', flexShrink: 0 }} />
        <Skeleton w={`${wA}%`} h={12} />
        <div style={{ flex: 1 }} />
        <Skeleton w={70} h={16} style={{ borderRadius: 999 }} />
        <Skeleton w={60} h={12} />
      </div>
    );
  }
  if (variant === 'company') {
    return (
      <div style={cell}>
        <Skeleton w={26} h={26} style={{ borderRadius: 6, flexShrink: 0 }} />
        <Skeleton w={`${wA}%`} h={12} />
        <div style={{ flex: 1 }} />
        <Skeleton w={90} h={16} style={{ borderRadius: 999 }} />
        <Skeleton w={70} h={12} />
      </div>
    );
  }
  // generic
  return (
    <div style={cell}>
      <Skeleton w={26} h={26} style={{ borderRadius: '50%', flexShrink: 0 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <Skeleton w={`${wA}%`} h={11} />
        <div style={{ marginTop: 6 }}>
          <Skeleton w={`${wB}%`} h={10} />
        </div>
      </div>
    </div>
  );
};

const SkeletonRows = ({ rows = 8, variant = 'generic' }) => (
  <div style={{ background: 'var(--surface)' }}>
    {Array.from({ length: rows }).map((_, i) => (
      <SkeletonRow key={i} i={i} variant={variant} />
    ))}
  </div>
);

// A record page is a stack of these. `collapsible` folds one to its header
// plus a one-line `preview` (Andres, 2026-09-23: the deal page had grown
// past a screen). The open state is remembered per viewer under `id`
// (localStorage — a convenience, never shared), and an 'atlas-sections'
// window event with {open} folds or unfolds every collapsible section at
// once (the Expand all / Collapse all control); an optional `ids` list
// scopes it to those sections (RecordPage's fold-all pair).
const sectionPrefKey = (id) => `atlas.section.${id}`;
const readSectionOpen = (id, fallback) => {
  try { const v = localStorage.getItem(sectionPrefKey(id)); return v == null ? fallback : v === '1'; } catch (_) { return fallback; }
};
const writeSectionOpen = (id, open) => { try { if (id) localStorage.setItem(sectionPrefKey(id), open ? '1' : '0'); } catch (_) { /* private mode */ } };

// Header fit (2026-10-21): nothing in a header leaves its card. The header
// wraps: a string title ellipsizes only when it alone is wider than the row,
// and the action group drops under the title (and wraps into rows) instead of
// running past the edge. A header that fits renders as it always has.
const SECTION_TITLE_FIT = { minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };
// A span/div action group gets wrap defaults; its own style keys win (a strip
// that must scroll sets flexWrap: 'nowrap'). cloneElement keeps its ref.
const SECTION_ACTION_FIT = { flexWrap: 'wrap', justifyContent: 'flex-end', rowGap: 6, maxWidth: '100%' };
const fitSectionAction = (a) => (a && typeof React.isValidElement === 'function' && React.isValidElement(a)
  && (a.type === 'span' || a.type === 'div')
  ? React.cloneElement(a, { style: { ...SECTION_ACTION_FIT, ...(a.props.style || {}) } }) : a);
// Narrow headers: the v2 record pages (240–440px side columns, drawers) and
// the phone pages. Cards with 3+ header items show one primary, ⤢ and ⋯ there.
// Read at call time; no hook.
const headerCompact = () => {
  const s = typeof window.recordSurface === 'function' ? window.recordSurface() : (dev().phone ? 'phone' : 'classic');
  return s === 'v2' || s === 'phone';
};

const Section = ({ title, action, children, style, collapsible = false, id, defaultOpen = true, preview, variant = 'card' }) => {
  // 'rail' (window.SummaryCard): flush right-rail card, rule underneath, rail header padding. Style only.
  const rail = variant === 'rail';
  const [open, setOpen] = React.useState(() => (collapsible && id ? readSectionOpen(id, defaultOpen) : true));
  React.useEffect(() => {
    if (!collapsible) return undefined;
    const onAll = (e) => { const d = e.detail || {}; if (Array.isArray(d.ids) && !d.ids.includes(id)) return; const want = !!d.open; setOpen(want); writeSectionOpen(id, want); };
    window.addEventListener('atlas-sections', onAll);
    return () => window.removeEventListener('atlas-sections', onAll);
  }, [collapsible, id]);
  const toggle = () => { const next = !open; setOpen(next); writeSectionOpen(id, next); };
  const showBody = !collapsible || open;
  const Chevron = window.Icon && window.Icon.ChevronR;
  return (
    <section style={{ background: 'var(--surface)', ...(rail ? { borderBottom: '1px solid var(--border)' } : { border: '1px solid var(--border)', borderRadius: 10 }), ...style }}>
      {title && (
        <header
          onClick={collapsible ? toggle : undefined}
          onKeyDown={collapsible ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(); } } : undefined}
          role={collapsible ? 'button' : undefined} tabIndex={collapsible ? 0 : undefined}
          aria-expanded={collapsible ? open : undefined}
          style={{ padding: rail ? '12px 14px 8px' : '10px 14px', borderBottom: showBody ? '1px solid var(--border)' : 'none', display: 'flex', alignItems: 'center',
                   flexWrap: 'wrap', columnGap: 8, rowGap: 6, cursor: collapsible ? 'pointer' : 'default', userSelect: collapsible ? 'none' : undefined, minWidth: 0,
                   ...(dev().phone ? { minHeight: 44, boxSizing: 'border-box' } : null) }}>
          {/* chevron + title + preview stay one group, so a wrap never strands the chevron */}
          <span data-section-head="" style={{ flex: '1 1 auto', minWidth: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
            {collapsible && (
              <span aria-hidden style={{ display: 'inline-flex', color: 'var(--text-4)', flexShrink: 0,
                                         transform: open ? 'rotate(90deg)' : 'none', transition: 'transform .15s ease' }}>
                {Chevron ? <Chevron size={11} /> : '›'}
              </span>
            )}
            <h3 style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', ...(typeof title === 'string' ? SECTION_TITLE_FIT : { flexShrink: 0 }) }}>{title}</h3>
            {/* width 0: the preview never widens the group, so it never pushes the actions to a second line */}
            {collapsible && !open && preview && (
              <span style={{ fontSize: 12, color: 'var(--text-3)', flex: 1, width: 0, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {preview}
              </span>
            )}
          </span>
          <div data-section-actions="" style={{ marginLeft: 'auto', flexShrink: 0, maxWidth: '100%' }} onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>{fitSectionAction(action)}</div>
        </header>
      )}
      {showBody && children}
    </section>
  );
};

// Fold or unfold every collapsible Section on the page at once.
const setAllSections = (open) => window.dispatchEvent(new CustomEvent('atlas-sections', { detail: { open } }));

// The phone pages' fold (step 15; one primitive for the deal, company and contact
// phone pages). One header button (≥52px): icon · title over a sub line (or, folded,
// the preview) · meta · chevron. The body mounts on first open and stays mounted
// (hidden) after, so a panel that fetches on mount pays once and keeps its state.
// `id` remembers the open state per viewer under atlas.section.<id>, the same
// memory as Section (phone ids are phone.* only). variant 'row' sits in a grouped
// list (a rule underneath); 'card' stands alone (rules above and below, 12px gap).
const LazyFold = ({ id, title, sub, meta, preview, icon, defaultOpen = false, variant = 'row', children }) => {
  const [open, setOpen] = React.useState(() => (id ? readSectionOpen(id, defaultOpen) : defaultOpen));
  const [mounted, setMounted] = React.useState(open);
  const toggle = () => { const next = !open; setOpen(next); if (next) setMounted(true); writeSectionOpen(id, next); };
  const line = sub || (!open ? preview : null);
  const Chevron = window.Icon && window.Icon.ChevronR;
  return (
    <div data-lazy-fold={id || ''} style={{ background: 'var(--surface)', borderBottom: '1px solid var(--border)',
      ...(variant === 'card' ? { marginTop: 12, borderTop: '1px solid var(--border)' } : null) }}>
      <button type="button" aria-expanded={open} onClick={toggle}
        style={{ WebkitTapHighlightColor: 'transparent', touchAction: 'manipulation', display: 'flex', alignItems: 'center', gap: 12,
                 width: '100%', minHeight: 52, padding: '8px 16px', border: 'none', background: 'transparent', textAlign: 'left',
                 cursor: 'pointer', color: 'var(--text)', fontFamily: 'inherit' }}>
        {icon && <span style={{ display: 'inline-flex', color: 'var(--text-3)', flexShrink: 0 }}>{icon}</span>}
        <span style={{ flex: 1, minWidth: 0 }}>
          <span style={{ display: 'block', fontSize: 15.5, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{title}</span>
          {line && <span style={{ display: 'block', fontSize: 13, color: 'var(--text-3)', marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{line}</span>}
        </span>
        {meta != null && <span style={{ fontSize: 13, color: 'var(--text-3)', flexShrink: 0 }}>{meta}</span>}
        <span aria-hidden style={{ display: 'inline-flex', color: 'var(--text-4)', flexShrink: 0, transform: open ? 'rotate(90deg)' : 'none', transition: 'transform .15s ease' }}>
          {Chevron ? <Chevron size={14} /> : '›'}
        </span>
      </button>
      {mounted && (
        <div style={{ display: open ? 'block' : 'none', borderTop: '1px solid var(--border)', overflowX: 'hidden' }}>{children}</div>
      )}
    </div>
  );
};

// Right-side slide-in drawer used for entity edits (Members, Teams, Industries).
// `zIndex` (default 50) lets a create-drawer stack ABOVE a modal (modals sit
// at 60) — e.g. "+ New contact" opened from the Mark-as-referred modal.
// `modeless`: no backdrop, page behind stays fully interactive (the territory
// radar drawer — dragging the pin / panning the map must not close it).
// Focus is not trapped in that mode; Escape and × still close.
// `widthKey`: the drawer remembers its own dragged width (atlas.drawerW.<key>)
// and opens at its own `width` until then; without one it shares atlas.drawerW.
//
// Phone: the same drawer presents as a full-screen sheet (slides up, safe-area
// header with a big close target, footer kept above the home indicator and
// the on-screen keyboard). Touch devices get no resize handle; on iPad the
// panel re-clamps to the window on rotation.
// Inside a workspace (LayerContext) it stacks at z70 above it, and is hidden
// with it (no Escape or focus entry while the workspace is hidden).
// Escape calls `onEscape` when given, else `onClose`.
// `top`: the wrapper's top edge (0 = the viewport); a modeless drawer inside a
// workspace passes the workspace header's height so the header stays reachable.
const Drawer = ({ open, onClose, onEscape, title, subtitle, footer, width = 480, widthKey, zIndex = 50, modeless = false, top = 0, children }) => {
  const layer = React.useContext(LayerContext);
  const z = _layerZ(zIndex, layer.zBase);
  const device = window.useDevice ? window.useDevice() : dev();
  const sheet = !!device.phone && !modeless;
  const panelRef = React.useRef(null);
  const backdropRef = React.useRef(null);
  const titleId = React.useId();
  // Stay mounted through the exit animation; `openRef` guards against a
  // re-open racing the exit's unmount.
  const [mounted, setMounted] = React.useState(open);
  const openRef = React.useRef(open);
  openRef.current = open;
  React.useEffect(() => {
    if (open) { setMounted(true); return; }
    if (!panelRef.current) { setMounted(false); return; }
    Promise.all([
      animateEl(panelRef.current, sheet ? { transform: 'translateY(24px)', opacity: 0 } : { transform: 'translateX(16px)', opacity: 0 }),
      animateEl(backdropRef.current, { opacity: 0 }),
    ]).then(() => { if (!openRef.current) setMounted(false); });
  }, [open]);
  React.useLayoutEffect(() => {
    if (!open || !mounted) return;
    // Motion commits the last keyframe as an inline style, and a transformed aside is
    // the containing block of every position:fixed descendant (a Drawer or a prompt
    // opened from inside it would be boxed into this one): once the slide-in settles,
    // the transform comes off.
    animateEl(panelRef.current, sheet
      ? { transform: ['translateY(24px)', 'translateY(0px)'], opacity: [0, 1] }
      : { transform: ['translateX(16px)', 'translateX(0px)'], opacity: [0, 1] })
      .then(() => { if (openRef.current && panelRef.current) panelRef.current.style.transform = ''; });
    animateEl(backdropRef.current, { opacity: [0, 1] });
  }, [open, mounted]);
  // Drag-to-resize from the left edge; the width persists per widthKey (else
  // in the one shared key, so widths saved before widthKey still apply).
  const LS = widthKey ? `atlas.drawerW.${widthKey}` : 'atlas.drawerW';
  const clampW = (v) => Math.max(340, Math.min((typeof window !== 'undefined' ? window.innerWidth : 1280) - 80, v));
  const [w, setW] = React.useState(() => {
    try { const s = parseInt(localStorage.getItem(LS), 10); if (s >= 340) return clampW(s); } catch (_) {}
    return clampW(width);
  });
  const dragging = React.useRef(false);
  useFocusTrap(open && !modeless && !layer.hidden, panelRef);
  useEscapeKey(open && !layer.hidden, onEscape || onClose);
  React.useEffect(() => {
    if (!open) return;
    const onMove = (e) => { if (dragging.current) setW(clampW(window.innerWidth - e.clientX)); };
    const onUp = () => {
      if (!dragging.current) return;
      dragging.current = false; document.body.style.userSelect = ''; document.body.style.cursor = '';
      try { if (panelRef.current) localStorage.setItem(LS, String(Math.round(panelRef.current.offsetWidth))); } catch (_) {}
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
  }, [open]);
  if (!mounted) return null;
  if (sheet) {
    // Portaled: a Drawer opened from inside a page (the deal page's note
    // composer) would otherwise be positioned against any ancestor that has
    // a transform, and render squeezed between the top bar and the tab bar.
    return ReactDOM.createPortal(
      <div style={{ position: 'fixed', inset: 0, zIndex: z, display: layer.hidden ? 'none' : 'flex', flexDirection: 'column',
        pointerEvents: !open ? 'none' : 'auto' }}>
        <div ref={backdropRef} onClick={onClose} style={{ position: 'absolute', inset: 0, background: 'oklch(0.2 0.01 270 / 0.18)' }} />
        <aside
          ref={panelRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby={title ? titleId : undefined}
          className="atlas-sheet"
          style={{
            position: 'relative', flex: 1, minHeight: 0, width: '100%',
            background: 'var(--surface)', display: 'flex', flexDirection: 'column',
            paddingLeft: 'var(--sal)', paddingRight: 'var(--sar)',
          }}
        >
          <header style={{ padding: 'calc(var(--sat) + 6px) 8px 8px 16px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <h2 id={titleId} style={{ fontSize: 17, fontWeight: 600, color: 'var(--text)', lineHeight: 1.25 }}>{title}</h2>
              {subtitle && <div style={{ fontSize: 13, color: 'var(--text-3)', marginTop: 2 }}>{subtitle}</div>}
            </div>
            <button onClick={onClose} aria-label="Close" style={{
              width: 44, height: 44, borderRadius: 10, border: 'none', flexShrink: 0,
              background: 'transparent', cursor: 'pointer', color: 'var(--text-2)',
              fontSize: 26, lineHeight: 1,
            }}>×</button>
          </header>
          <div style={{ flex: 1, minHeight: 0, overflow: 'auto', WebkitOverflowScrolling: 'touch', padding: '14px 16px',
                        paddingBottom: footer ? 14 : 'calc(14px + var(--sab))' }}>{children}</div>
          {footer && (
            <footer style={{ padding: '10px 16px', paddingBottom: 'calc(10px + max(var(--sab), var(--kb, 0px)))', borderTop: '1px solid var(--border)', display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap', flexShrink: 0, background: 'var(--surface)' }}>
              {footer}
            </footer>
          )}
        </aside>
      </div>,
      document.body
    );
  }
  // Touch (iPad): no drag handle, and the stored width re-clamps to the
  // current window so a rotation to portrait never leaves it overflowing.
  const panelW = device.touch ? Math.max(320, Math.min(w, (window.innerWidth || 1024) - 48)) : w;
  return (
    <div style={{ position: 'fixed', inset: 0, ...(top ? { top } : null), zIndex: z, display: layer.hidden ? 'none' : 'flex', justifyContent: 'flex-end',
      pointerEvents: modeless || !open ? 'none' : 'auto' }}>
      {!modeless && (
        <div ref={backdropRef} onClick={onClose} style={{ position: 'absolute', inset: 0, background: 'oklch(0.2 0.01 270 / 0.18)' }} />
      )}
      <aside
        ref={panelRef}
        role="dialog"
        aria-modal={modeless ? undefined : 'true'}
        aria-labelledby={title ? titleId : undefined}
        style={{
          position: 'relative', width: panelW, maxWidth: '100%', height: '100%',
          background: 'var(--surface)', borderLeft: '1px solid var(--border-strong)',
          boxShadow: 'var(--shadow-lg)', display: 'flex', flexDirection: 'column',
          pointerEvents: 'auto',
          ...(device.touch ? { paddingTop: 'var(--sat)', paddingBottom: 'var(--sab)', paddingRight: 'var(--sar)' } : null),
        }}
      >
        {!device.touch && <div
          onMouseDown={(e) => { dragging.current = true; document.body.style.userSelect = 'none'; document.body.style.cursor = 'ew-resize'; e.preventDefault(); }}
          title="Drag to resize"
          style={{ position: 'absolute', left: -3, top: 0, bottom: 0, width: 7, cursor: 'ew-resize', zIndex: 3 }}
        />}
        <header style={{ padding: '14px 16px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'flex-start', gap: 10 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h2 id={titleId} style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>{title}</h2>
            {subtitle && <div style={{ fontSize: 11.5, color: 'var(--text-3)', marginTop: 2 }}>{subtitle}</div>}
          </div>
          <button onClick={onClose} aria-label="Close" style={{
            width: device.touch ? 36 : 26, height: device.touch ? 36 : 26, borderRadius: 5, border: 'none',
            background: 'transparent', cursor: 'pointer', color: 'var(--text-3)',
            fontSize: device.touch ? 22 : 18, lineHeight: 1, flexShrink: 0,
          }}>×</button>
        </header>
        <div style={{ flex: 1, overflow: 'auto', padding: '14px 16px' }}>{children}</div>
        {footer && (
          <footer style={{ padding: '10px 16px', borderTop: '1px solid var(--border)', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            {footer}
          </footer>
        )}
      </aside>
    </div>
  );
};

// Who may be picked as the OWNER of a company / contact / deal: active and
// flagged owner_eligible in Settings → Users (202609111300). Task assignees
// and the deal's Associate slot use plain `active` — associates belong there.
const isOwnerEligible = (m) => !!m && m.active !== false && m.owner_eligible === true;
window.isOwnerEligible = isOwnerEligible;

const Field = ({ label, hint, children }) => (
  <label style={{ display: 'block', marginBottom: 12 }}>
    <div style={{ fontSize: 10.5, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 500, marginBottom: 4 }}>{label}</div>
    {children}
    {hint && <div style={{ fontSize: 10.5, color: 'var(--text-4)', marginTop: 3 }}>{hint}</div>}
  </label>
);

const TextInput = (props) => (
  <input
    {...props}
    style={{
      width: '100%', padding: '6px 8px', fontSize: 12.5,
      border: '1px solid var(--border)', borderRadius: 5,
      background: 'var(--surface)', color: 'var(--text)',
      ...(dev().phone ? { padding: '9px 11px', borderRadius: 8 } : null),
      ...(props.style || {}),
    }}
  />
);

// ── MoneyInput — a TextInput that speaks shorthand ──────────────────────
// For absolute-USD fields in the create drawers. "4M", "100k", "$1.5M" and
// "4,000,000" all land as the full number (window.parseNumInput); plain
// digits behave exactly as before. It's type="text" because
// <input type="number"> refuses the letter outright — that control is what
// silently turned "4M" into 4. inputMode="decimal" keeps the numeric keypad
// on mobile.
//
// Raw keystrokes stay in local state so the field isn't fought mid-word. On
// blur the text normalises to comma-grouped digits, which is the
// confirmation that "4M" was read as 4,000,000. Text that doesn't parse
// reverts to the last good value rather than committing a wrong number.
//
// `value` is a number | null and `onChange` is called with a number | null —
// NOT an event, since there's no raw string worth handing back.
const MoneyInput = ({ value, onChange, placeholder, hint = true, ...rest }) => {
  const [raw, setRaw]         = React.useState(value == null ? '' : window.formatNumInput(value));
  const [focused, setFocused] = React.useState(false);
  const parsed = window.parseNumInput(raw);

  const onType = (e) => {
    setRaw(e.target.value);
    const next = window.parseNumInput(e.target.value);
    // Mid-word garbage ("4M" one keystroke from "4Mi") just doesn't move the
    // committed value — it never overwrites it with a partial read.
    if (next !== undefined) onChange(next);
  };
  const onBlur = () => {
    setFocused(false);
    if (parsed === undefined) { setRaw(value == null ? '' : window.formatNumInput(value)); return; }
    setRaw(parsed == null ? '' : window.formatNumInput(parsed));
    onChange(parsed);
  };

  return (
    <>
      <TextInput
        {...rest}
        type="text" inputMode="decimal" value={raw} placeholder={placeholder}
        onChange={onType} onFocus={() => setFocused(true)} onBlur={onBlur}
      />
      {/* Live read-back while they type, so the interpretation is visible
          before the drawer is submitted. Focused-only: a drawer full of
          standing hints is noise. */}
      {hint && focused && raw.trim() !== '' && (
        <div style={{ fontSize: 10.5, marginTop: 3,
                      color: parsed === undefined ? 'var(--rose-fg)' : 'var(--text-4)' }}>
          {parsed === undefined ? "Not a number — try 4M, 100k or 4000000" : `= ${window.fmtMoney(parsed)}`}
        </div>
      )}
    </>
  );
};

// ── Combobox — replaces native <select> with a custom dropdown ───────────
// Drop-in compatible with the old Select primitive: still accepts
// {value, onChange, options}. onChange receives an event-shaped object so
// existing handlers (`(e) => setX(e.target.value)`) keep working; it also
// carries `label` for callers that need the picked option's display text.
//
// `loadOptions` (async q => [{value, label}]) switches the menu to
// server-backed search: typing queries the API instead of filtering a
// pre-loaded slice, so records outside any client-side cap stay findable.
// Static `options` (e.g. "(none)", presets) stay pinned above the results.
// autoOpen: mount with the menu already open — for hosts that swap the
// Combobox in as an edit mode (grid cells), where a second click to open
// the menu would feel broken. Those hosts also pass `onClose` (fired
// whenever the menu closes — pick, Escape, outside click, trigger toggle)
// so they can drop back to their read view, and `dense` for a trigger
// sized like a grid input. Options may carry a `hue` (Pill token); the
// menu renders it as a colored dot so stage lists read like their badges.
// keepOpen: don't close after a pick — for hosts that take several values
// from one open (the deal team add-box), where the host removes what it
// just took from `options`. onChange still fires per pick; the query resets
// and focus stays in the search box, so the next pick needs no re-click.
// Escape / outside click / trigger toggle still close. menuMaxHeight and
// menuMinWidth raise the menu's size caps (default 320 / 240) for hosts
// that want to show more rows at once; both stay clamped to the viewport,
// so the menu still flips up when there is no room below.
// footer: ({ query, close }) => node — an action row pinned under the
// results (e.g. "+ New company “q”" when the search comes up empty). It
// lives inside the portaled menu so it is never hidden behind it; the
// callback gets `close` to dismiss the menu before opening something else.
// selectedValues: an array marks several rows as picked (checkmarks) — the
// multi-select mode window.Picker builds on with keepOpen. display: what the
// trigger shows instead of the picked label (e.g. "Miami, Doral +2").
// An option with `disabled` shows but can't be picked.
// searchAlways: show the search box however short the list (a creatable
// picker needs somewhere to type the new value).
const Combobox = ({ value, onChange, options = [], placeholder = 'Select…', searchable, disabled = false, style, loadOptions, autoOpen = false, onClose, dense = false, keepOpen = false, menuMaxHeight, menuMinWidth, footer, selectedValues, display, searchAlways = false }) => {
  const [open, setOpen] = React.useState(!!autoOpen);
  const layer = React.useContext(LayerContext);
  const menuZ = _layerZ(400, layer.zBase);   // 400 already beats 70; kept for the rule
  const [query, setQuery] = React.useState('');
  const [highlight, setHighlight] = React.useState(0);
  const [pos, setPos] = React.useState(null);
  const [remote, setRemote] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const rootRef = React.useRef(null);
  const menuRef = React.useRef(null);
  const inputRef = React.useRef(null);
  // Latest loadOptions without re-triggering the fetch effect — call sites
  // pass inline arrows whose identity changes every render.
  const loadRef = React.useRef(loadOptions);
  loadRef.current = loadOptions;
  // Remember the last chosen remote option so the trigger button can render
  // its label after the search results it came from are gone.
  const pickedRef = React.useRef(null);
  // Phone: the menu is a bottom sheet instead of an anchored popover — a
  // 240px menu pinned to a 30px trigger is a precision task by finger, and
  // option descriptions (hover tooltips on desktop) show inline there.
  const sheet = !!dev().phone;

  // The menu is portaled to <body> with fixed positioning so it can escape
  // overflow:auto panels (narrow sidebars, drawers) instead of being clipped
  // by them or stretching their scroll area. Flips upward when the anchor is
  // near the bottom of the viewport.
  const place = React.useCallback(() => {
    const el = rootRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const vw = window.innerWidth, vh = window.innerHeight;
    const width = Math.min(Math.max(r.width, menuMinWidth || 240), vw - 16);
    const left = Math.max(8, Math.min(r.left, vw - width - 8));
    const below = vh - r.bottom - 12;
    const above = r.top - 12;
    const dropUp = below < 160 && above > below;
    const maxHeight = Math.max(120, Math.min(menuMaxHeight || 320, dropUp ? above : below));
    setPos(dropUp
      ? { left, width, maxHeight, bottom: vh - r.top + 4 }
      : { left, width, maxHeight, top: r.bottom + 4 });
  }, [menuMinWidth, menuMaxHeight]);

  React.useLayoutEffect(() => {
    if (open) place();
  }, [open, place]);

  React.useEffect(() => {
    if (!open) return;
    // Capture-phase scroll so the menu tracks the anchor when any ancestor
    // panel scrolls, not just the window.
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [open, place]);

  // Server-backed search: fetch on open (starter list) and on each keystroke
  // (debounced). Stale responses are dropped so fast typing can't interleave.
  React.useEffect(() => {
    if (!loadRef.current || !open) return;
    let stale = false;
    setLoading(true);
    const q = query.trim();
    const t = setTimeout(() => {
      Promise.resolve(loadRef.current(q))
        .then((opts) => { if (!stale) { setRemote(Array.isArray(opts) ? opts : []); setHighlight(0); } })
        .catch(() => { if (!stale) setRemote([]); })
        .then(() => { if (!stale) setLoading(false); });
    }, q ? 250 : 0);
    return () => { stale = true; clearTimeout(t); };
  }, [open, query]);

  // Options may include {header: 'Label'} section markers (non-selectable).
  // An option's optional `description` renders as an ⓘ hint in the menu (the
  // label stays short) instead of being crammed into the label text. `hint`
  // is short disambiguating context (e.g. a contact's company) rendered
  // inline and muted next to the label — visible without hovering.
  // A default parameter only covers undefined; callers that pass a not-yet-
  // loaded `null` must not crash the page (Sentry IPRO-ATLAS-WEB-N).
  const norm = (Array.isArray(options) ? options : []).map((o) => typeof o === 'string'
    ? { value: o, label: o }
    : (o && o.header != null ? { header: o.header } : { value: o.value, label: o.label, description: o.description, hint: o.hint, hue: o.hue, disabled: !!o.disabled }));
  const selectableAll = norm.filter((o) => o.header == null);
  const enableSearch = !!loadOptions || searchAlways || (searchable !== false && (keepOpen || selectableAll.length > 6));
  const q = query.trim().toLowerCase();
  // While searching, show a flat list of matches (section headers dropped).
  let filtered = (!enableSearch || !q)
    ? norm
    : selectableAll.filter((o) =>
        (o.label || '').toLowerCase().includes(q) || (o.description || '').toLowerCase().includes(q)
        || (o.hint || '').toLowerCase().includes(q));
  if (loadOptions) {
    // Static options (client-filtered above) pinned first, then the server
    // results minus anything the static list already covers.
    const seen = new Set(filtered.filter((o) => o.header == null).map((o) => String(o.value)));
    filtered = [...filtered, ...remote.filter((o) => o && !seen.has(String(o.value)))];
  }
  const selectable = filtered.filter((o) => o.header == null);
  const pickedSet = Array.isArray(selectedValues) ? new Set(selectedValues.map(String)) : null;
  const isPicked = (opt) => (pickedSet ? pickedSet.has(String(opt.value))
    : !!(selected && String(selected.value) === String(opt.value)));
  const selected =
    selectableAll.find((o) => String(o.value) === String(value))
    || remote.find((o) => o && String(o.value) === String(value))
    || (pickedRef.current && String(pickedRef.current.value) === String(value) ? pickedRef.current : null);

  React.useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (rootRef.current?.contains(e.target)) return;
      if (menuRef.current?.contains(e.target)) return;
      close();
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('touchstart', onDoc, { passive: true });
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('touchstart', onDoc);
    };
  }, [open]);

  // The menu only mounts once positioned (pos set), so key focus off that.
  // Without a search box the menu itself takes focus, so arrow keys / Enter
  // / Escape work the moment it opens (an autoOpen grid cell has no other
  // focused element to route keys through).
  const menuMounted = open && pos != null;
  React.useEffect(() => {
    if (!menuMounted) return;
    // Phone: a focused search box raises the keyboard over the list — only
    // worth it when the list is a server search that needs typing.
    if (sheet) { if (loadOptions) inputRef.current?.focus(); return; }
    if (enableSearch) inputRef.current?.focus(); else menuRef.current?.focus();
  }, [menuMounted, enableSearch]);
  // Open on the current value, not the first row — keyboard users then step
  // from where they are, and a long list scrolls the pick into view.
  React.useEffect(() => {
    if (!menuMounted || !selected) return;
    const idx = selectable.findIndex((o) => String(o.value) === String(selected.value));
    if (idx > 0) {
      setHighlight(idx);
      menuRef.current?.querySelector('[data-selected]')?.scrollIntoView({ block: 'nearest' });
    }
  }, [menuMounted]);

  const close = () => {
    setOpen(false); setQuery(''); setHighlight(0); setPos(null);
    onClose?.();
  };
  const choose = (opt) => {
    if (!opt || opt.disabled) return;
    pickedRef.current = opt;
    onChange?.({ target: { value: opt.value }, value: opt.value, label: opt.label });
    // keepOpen hosts take several values from one open: reset the query and
    // highlight the way close() would, but leave the menu up and the caret
    // in the search box so the next pick is one click. The row just clicked
    // is about to disappear (the host drops what it took from `options`).
    if (keepOpen) { setQuery(''); setHighlight(0); inputRef.current?.focus(); return; }
    close();
  };
  // Its workspace was hidden (LayerContext): drop the body-portaled menu with it.
  React.useEffect(() => { if (layer.hidden && open) close(); }, [layer.hidden]);   // eslint-disable-line react-hooks/exhaustive-deps
  const onKey = (e) => {
    // Escape closes just the menu — not the popover/dialog hosting it.
    if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); close(); return; }
    if (!selectable.length) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setHighlight((h) => Math.min(selectable.length - 1, h + 1)); }
    if (e.key === 'ArrowUp')   { e.preventDefault(); setHighlight((h) => Math.max(0, h - 1)); }
    if (e.key === 'Enter')     { e.preventDefault(); choose(selectable[highlight]); }
  };

  return (
    <div ref={rootRef} style={{ position: 'relative', ...(style || {}) }}>
      <button
        type="button"
        onClick={() => { if (disabled) return; if (open) close(); else setOpen(true); }}
        disabled={disabled}
        title={(selected && selected.description) || undefined}
        style={{
          width: '100%', fontSize: sheet ? 15 : dense ? 12 : 12.5,
          padding: dense ? '3px 22px 3px 6px' : '6px 28px 6px 8px',
          ...(sheet ? { minHeight: dense ? 36 : 42 } : dev().touch ? { minHeight: dense ? 30 : 34 } : null),
          border: '1px solid ' + (open ? 'var(--accent)' : 'var(--border)'),
          borderRadius: 5, background: 'var(--surface)',
          color: (selected || (display != null && display !== '')) ? 'var(--text)' : 'var(--text-3)',
          cursor: disabled ? 'not-allowed' : 'pointer', textAlign: 'left',
          display: 'flex', alignItems: 'center', gap: 6, position: 'relative',
          transition: 'border-color .12s',
          opacity: disabled ? 0.6 : 1,
        }}
      >
        <PhoneHit />
        {selected && selected.hue && (
          <span style={{ width: 7, height: 7, borderRadius: 999, flexShrink: 0, background: (HUE[selected.hue] || HUE.slate).dot }} />
        )}
        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {display != null && display !== '' ? display : (selected ? selected.label : placeholder)}
        </span>
        <Icon.ChevronR size={11} color="var(--text-4)" style={{ transform: open ? 'rotate(90deg)' : 'rotate(0)' }} />
      </button>
      {open && pos && sheet && ReactDOM.createPortal(
        <div data-combobox-menu="" style={{ position: 'fixed', inset: 0, zIndex: menuZ, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
          <div onClick={close} style={{ position: 'absolute', inset: 0, background: 'oklch(0.2 0.01 270 / 0.28)' }} />
          <div
            ref={menuRef}
            role="listbox"
            tabIndex={-1}
            onKeyDown={onKey}
            className="atlas-sheet"
            style={{
              outline: 'none', position: 'relative',
              marginBottom: 'var(--kb, 0px)',
              maxHeight: 'calc(min(var(--vvh, 100vh), 100vh) * 0.78)',
              background: 'var(--surface)', borderRadius: '14px 14px 0 0',
              boxShadow: 'var(--shadow-lg)', display: 'flex', flexDirection: 'column',
              paddingBottom: 'var(--sab)', paddingLeft: 'var(--sal)', paddingRight: 'var(--sar)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 8px 8px 16px', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
              <div style={{ flex: 1, minWidth: 0, fontSize: 15, fontWeight: 600, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {placeholder && placeholder !== 'Select…' ? placeholder : 'Choose'}
              </div>
              <button type="button" onClick={close} style={{ height: 44, minWidth: 44, padding: '0 12px', border: 'none', background: 'transparent', color: 'var(--accent-text)', fontSize: 15, fontWeight: 600, cursor: 'pointer' }}>
                {keepOpen ? 'Done' : 'Cancel'}
              </button>
            </div>
            {enableSearch && (
              <div style={{ padding: '8px 12px', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
                <input
                  ref={inputRef}
                  type="search"
                  enterKeyHint="search"
                  autoCapitalize="off" autoCorrect="off" spellCheck={false}
                  value={query}
                  onChange={(e) => { setQuery(e.target.value); setHighlight(0); }}
                  placeholder={loadOptions ? 'Search…' : 'Filter…'}
                  style={{
                    width: '100%', padding: '9px 12px', fontSize: 16,
                    border: '1px solid var(--border)', borderRadius: 10,
                    background: 'var(--surface-2)', color: 'var(--text)',
                  }}
                />
              </div>
            )}
            <div style={{ overflow: 'auto', flex: 1, minHeight: 0, WebkitOverflowScrolling: 'touch' }}>
              {selectable.length === 0 && (
                <div style={{ padding: '16px', fontSize: 14, color: 'var(--text-4)' }}>
                  {loading ? 'Searching…' : 'No matches.'}
                </div>
              )}
              {filtered.map((opt, i) => {
                if (opt.header != null) {
                  return (
                    <div key={'h' + i} style={{ padding: '12px 16px 4px', fontSize: 11.5, fontWeight: 600, color: 'var(--text-4)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{opt.header}</div>
                  );
                }
                const sel = isPicked(opt);
                return (
                  <button
                    key={String(opt.value) + '_' + i}
                    type="button"
                    onClick={() => choose(opt)}
                    disabled={opt.disabled}
                    data-selected={sel ? '' : undefined}
                    style={{
                      opacity: opt.disabled ? 0.45 : 1,
                      display: 'flex', alignItems: 'center', gap: 10, width: '100%', minHeight: 48,
                      padding: '8px 16px', border: 'none', borderBottom: '1px solid var(--border)', textAlign: 'left',
                      background: sel ? 'var(--accent-soft)' : 'transparent',
                      color: sel ? 'var(--accent-text)' : 'var(--text)',
                      fontSize: 15, cursor: 'pointer', fontWeight: sel ? 600 : 400,
                    }}
                  >
                    {opt.hue && (
                      <span style={{ width: 9, height: 9, borderRadius: 999, flexShrink: 0, background: (HUE[opt.hue] || HUE.slate).dot }} />
                    )}
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{opt.label}</span>
                      {(opt.hint || opt.description) && (
                        <span style={{ display: 'block', fontSize: 12.5, color: 'var(--text-3)', fontWeight: 400, marginTop: 1, lineHeight: 1.35 }}>
                          {[opt.hint, opt.description].filter(Boolean).join(' · ')}
                        </span>
                      )}
                    </span>
                    {sel && <Icon.Check size={15} color="var(--accent-text)" />}
                  </button>
                );
              })}
            </div>
            {footer && (
              <div style={{ borderTop: '1px solid var(--border)', flexShrink: 0 }}>
                {footer({ query: query.trim(), close })}
              </div>
            )}
          </div>
        </div>,
        document.body
      )}
      {open && pos && !sheet && ReactDOM.createPortal(
        <div
          ref={menuRef}
          data-combobox-menu=""
          /* ^ lets outside-click closers (e.g. FilterPopover) recognize
             clicks landing in this portaled menu as "inside" */
          tabIndex={-1}
          onKeyDown={onKey}
          style={{
            outline: 'none',
            position: 'fixed', left: pos.left, width: pos.width,
            ...(pos.top != null ? { top: pos.top } : { bottom: pos.bottom }),
            zIndex: menuZ /* above dialogs (300) — comboboxes render inside them */,
            background: 'var(--surface)', border: '1px solid var(--border-strong)',
            borderRadius: 7, boxShadow: 'var(--shadow-lg)', overflow: 'hidden',
            maxHeight: pos.maxHeight, display: 'flex', flexDirection: 'column',
          }}
        >
          {enableSearch && (
            <div style={{ padding: 6, borderBottom: '1px solid var(--border)' }}>
              <input
                ref={inputRef}
                value={query}
                onChange={(e) => { setQuery(e.target.value); setHighlight(0); }}
                placeholder={loadOptions ? 'Search…' : 'Filter…'}
                style={{
                  width: '100%', padding: '4px 7px', fontSize: 11.5,
                  border: '1px solid var(--border)', borderRadius: 5,
                  background: 'var(--surface-2)',
                }}
              />
            </div>
          )}
          <div style={{ overflow: 'auto', flex: 1 }}>
            {selectable.length === 0 && (
              <div style={{ padding: '10px 12px', fontSize: 11.5, color: 'var(--text-4)' }}>
                {loading ? 'Searching…' : 'No matches.'}
              </div>
            )}
            {(() => { let si = -1; return filtered.map((opt, i) => {
              if (opt.header != null) {
                return (
                  <div key={'h' + i} style={{ padding: '7px 10px 3px', fontSize: 10, fontWeight: 600, color: 'var(--text-4)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{opt.header}</div>
                );
              }
              si += 1; const idx = si;
              const sel = isPicked(opt);
              const hi = idx === highlight;
              return (
                <button
                  key={String(opt.value) + '_' + i}
                  type="button"
                  onClick={() => choose(opt)}
                  onMouseEnter={() => setHighlight(idx)}
                  title={opt.description || undefined}
                  disabled={opt.disabled}
                  data-selected={sel ? '' : undefined}
                  style={{
                    opacity: opt.disabled ? 0.45 : 1,
                    display: 'flex', alignItems: 'center', gap: 6, width: '100%',
                    padding: '6px 10px', border: 'none', textAlign: 'left',
                    background: sel ? 'var(--accent-soft)' : (hi ? 'var(--surface-hover)' : 'transparent'),
                    color: sel ? 'var(--accent-text)' : 'var(--text)',
                    fontSize: 12.5, cursor: 'pointer', fontWeight: sel ? 600 : 400,
                  }}
                >
                  {opt.hue && (
                    <span style={{ width: 7, height: 7, borderRadius: 999, flexShrink: 0, background: (HUE[opt.hue] || HUE.slate).dot }} />
                  )}
                  <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{opt.label}</span>
                  {opt.hint && (
                    <span style={{ maxWidth: '50%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 11, color: sel ? 'var(--accent-text)' : 'var(--text-3)', fontWeight: 400 }}>
                      {opt.hint}
                    </span>
                  )}
                  {opt.description && <Icon.Help size={11} color={sel ? 'var(--accent-text)' : 'var(--text-4)'} />}
                  {sel && <Icon.Check size={11} color="var(--accent-text)" />}
                </button>
              );
            }); })()}
          </div>
          {footer && (
            <div style={{ borderTop: '1px solid var(--border)', flexShrink: 0 }}>
              {footer({ query: query.trim(), close })}
            </div>
          )}
        </div>,
        document.body
      )}
    </div>
  );
};

// Backward-compat alias so existing call sites that say <Select> keep working.
const Select = Combobox;

// ── Toast system ─────────────────────────────────────────────────────────
// Dispatches via a module-level subscriber list. ToastHost mounts once at
// the app root and renders the active stack.
const __TOAST_LISTENERS = new Set();
let __toastSeq = 0;
// `action` = { label, onClick, title? } adds one text button to the toast — the
// Undo that follows a quiet bulk action. Clicking it closes the toast first.
const pushToast = ({ type = 'info', title, body, duration = 4000, action } = {}) => {
  const id = ++__toastSeq;
  const t = { id, type, title, body, duration, action };
  __TOAST_LISTENERS.forEach((fn) => fn({ kind: 'add', toast: t }));
  if (duration > 0) {
    setTimeout(() => {
      __TOAST_LISTENERS.forEach((fn) => fn({ kind: 'remove', id }));
    }, duration);
  }
  return id;
};
const dismissToast = (id) =>
  __TOAST_LISTENERS.forEach((fn) => fn({ kind: 'remove', id }));

const TOAST_HUE = {
  info:    { bg: 'var(--surface)', fg: 'var(--text)',   accent: 'var(--accent)' },
  success: { bg: 'var(--green-bg)', fg: 'var(--green-fg)', accent: 'oklch(0.55 0.14 155)' },
  warning: { bg: 'var(--amber-bg)', fg: 'var(--amber-fg)', accent: 'oklch(0.65 0.13 75)' },
  error:   { bg: 'var(--rose-bg)',  fg: 'var(--rose-fg)',  accent: 'oklch(0.55 0.14 25)' },
};

const ToastCard = ({ t, onGone }) => {
  const ref = React.useRef(null);
  const phone = dev().phone;
  const tone = TOAST_HUE[t.type] || TOAST_HUE.info;
  React.useLayoutEffect(() => {
    animateEl(ref.current, { transform: ['translateY(8px)', 'translateY(0px)'], opacity: [0, 1] });
  }, []);
  React.useEffect(() => {
    if (!t.exiting) return;
    animateEl(ref.current, { transform: 'translateY(6px)', opacity: 0 }).then(() => onGone(t.id));
  }, [t.exiting]);
  return (
    <div ref={ref} style={{
      background: tone.bg, color: tone.fg,
      border: '1px solid var(--border-strong)', borderLeft: `3px solid ${tone.accent}`,
      borderRadius: 8, padding: '10px 14px', minWidth: phone ? 0 : 280, maxWidth: phone ? 'none' : 380,
      boxShadow: 'var(--shadow-lg)', pointerEvents: t.exiting ? 'none' : 'auto',
      display: 'flex', alignItems: 'flex-start', gap: 10,
    }}
      // Phone: tap anywhere on the toast to dismiss it (the × is tiny).
      onClick={phone ? () => dismissToast(t.id) : undefined}>
      <div style={{ flex: 1, minWidth: 0 }}>
        {t.title && <div style={{ fontSize: phone ? 14 : 12.5, fontWeight: 600 }}>{t.title}</div>}
        {t.body && <div style={{ fontSize: phone ? 13 : 11.5, color: tone.fg, opacity: 0.85, marginTop: t.title ? 2 : 0 }}>{t.body}</div>}
      </div>
      {/* Phone: a finger-sized target that keeps the same spot — a near miss
          lands on the card, which dismisses the toast and its action. It grows
          up, down and left only: the × sits 10px to its right. */}
      {t.action && (
        <button onClick={(e) => { e.stopPropagation(); dismissToast(t.id); t.action.onClick(); }} title={t.action.title} style={{
          border: 'none', background: 'transparent', color: tone.fg, cursor: 'pointer', flexShrink: 0,
          fontSize: phone ? 14 : 12.5, fontWeight: 600, padding: 0, textDecoration: 'underline',
          ...(phone ? { padding: '10px 0 10px 12px', margin: '-10px 0 -10px -12px' } : null),
        }}>{t.action.label}</button>
      )}
      <button onClick={() => dismissToast(t.id)} style={{
        border: 'none', background: 'transparent', color: tone.fg, opacity: 0.6,
        cursor: 'pointer', fontSize: 14, lineHeight: 1, padding: 0, flexShrink: 0,
      }}>×</button>
    </div>
  );
};

const ToastHost = () => {
  const [toasts, setToasts] = React.useState([]);
  React.useEffect(() => {
    const fn = (ev) => {
      if (ev.kind === 'add')    setToasts((xs) => [...xs, ev.toast]);
      // A remove only flags the toast; ToastCard animates out and then
      // reports back via onGone, which does the actual drop.
      if (ev.kind === 'remove') setToasts((xs) => xs.map((t) => t.id === ev.id ? { ...t, exiting: true } : t));
    };
    __TOAST_LISTENERS.add(fn);
    return () => { __TOAST_LISTENERS.delete(fn); };
  }, []);
  const onGone = React.useCallback((id) => setToasts((xs) => xs.filter((t) => t.id !== id)), []);
  // The host stays mounted while empty: a live region has to exist before a
  // toast lands in it for screen readers to announce it. z 600 sits over the
  // modals (≤ 500): an Undo toast must stay clickable when a save opens a
  // prompt (the list's hold-delay picker, z 300).
  // Phone: full-width cards above the tab bar and the home indicator.
  const phone = dev().phone;
  return (
    <div role="status" aria-live="polite" style={phone ? {
      position: 'fixed', left: 12, right: 12, zIndex: 600,
      bottom: 'calc(var(--tabbar-h) + var(--sab) + 10px)',
      display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'stretch',
      pointerEvents: 'none',
    } : {
      position: 'fixed', right: 18, bottom: 18, zIndex: 600,
      display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-end',
      pointerEvents: 'none',
    }}>
      {toasts.map((t) => <ToastCard key={t.id} t={t} onGone={onGone} />)}
    </div>
  );
};

// ── Confirm dialog — promise-based replacement for window.confirm ────────
// Two calling styles: await the returned promise, OR pass `onConfirm` (five
// call sites kept that shape from the old callback API and silently no-op'd
// once this went promise-based — the callback now actually fires).
let __confirmHostHandler = null;
const showConfirm = ({ title, body, confirmLabel = 'Confirm', cancelLabel = 'Cancel', danger = false, onConfirm = null } = {}) =>
  new Promise((resolve) => {
    const settle = (ok) => { if (ok && onConfirm) onConfirm(); resolve(ok); };
    if (!__confirmHostHandler) { settle(window.confirm(body || title || 'Confirm?')); return; }
    __confirmHostHandler({ title, body, confirmLabel, cancelLabel, danger, resolve: settle });
  });

const ConfirmHost = () => {
  const [state, setState] = React.useState(null);
  const dialogRef = React.useRef(null);
  const titleId = React.useId();
  const bodyId  = React.useId();
  React.useEffect(() => {
    __confirmHostHandler = (s) => setState(s);
    return () => { __confirmHostHandler = null; };
  }, []);
  const finish = React.useCallback((ok) => {
    setState((s) => { if (s) s.resolve(ok); return null; });
  }, []);
  useFocusTrap(!!state, dialogRef);
  useEscapeKey(!!state, () => finish(false));
  if (!state) return null;
  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 300,
      background: 'oklch(0.2 0.01 270 / 0.28)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}
      onClick={() => finish(false)}
    >
      <div
        ref={dialogRef}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={state.title ? titleId : undefined}
        aria-describedby={state.body ? bodyId : undefined}
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--surface)', border: '1px solid var(--border-strong)',
          borderRadius: 10, boxShadow: 'var(--shadow-lg)',
          width: 380, maxWidth: '92vw', padding: '16px 18px',
        }}
      >
        {state.title && <h3 id={titleId} style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>{state.title}</h3>}
        {state.body && <div id={bodyId} style={{ fontSize: 12.5, color: 'var(--text-2)', marginTop: state.title ? 6 : 0, lineHeight: 1.45 }}>{state.body}</div>}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 14 }}>
          <Btn size="sm" variant="outline" onClick={() => finish(false)}>{state.cancelLabel}</Btn>
          <Btn size="sm" variant={state.danger ? 'primary' : 'accent'}
            style={state.danger ? { background: 'oklch(0.50 0.14 25)', borderColor: 'oklch(0.50 0.14 25)' } : null}
            onClick={() => finish(true)}>{state.confirmLabel}</Btn>
        </div>
      </div>
    </div>
  );
};

// ── NoteModal ──────────────────────────────────────────────────────────────
// One canonical popup for inspecting (and editing, when allowed) a note.
// Used from the workspace Notes view, company/contact/deal detail panels,
// and the Integrations Activity dashboard. Refetches by id on open so it
// always shows current data after an edit.
//
// Rules:
//   • Integration-sourced notes (external_source != null) are read-only.
//     Bodies are owned by the upstream sync — editing here would just get
//     overwritten on the next pull. We DO let users edit subtype + pin
//     state for them, since those are local-only.
//   • Plain Atlas-authored notes are fully editable (title, body, type,
//     subtype, pin) with a confirm-delete in the footer.
//   • Linked company/contact pills jump to detail views via window.AtlasNav.
// {value,label} pairs — value is the DealCloud Note.Type choice NAME verbatim
// (what notes.dc_note_subtype stores and the write-back maps to a choice id).
// Safe non-KPI options first; the two call subtypes feed the D/S / B/S call
// KPI dashboards on both CRMs, so their labels say so — picking one logs a
// call as HELD (see kpi_call_parity in KNOWLEDGE.md).
// Labels stay short; the semantics live in `description`, which Combobox
// renders as an ⓘ hover hint instead of cramming it into the option text.
const NOTE_SUBTYPES = [
  { value: 'UPDATE',                 label: 'Update', description: 'Status / campaign update.' },
  { value: 'Email',                  label: 'Email' },
  { value: 'Other',                  label: 'Other' },
  // Atlas-only (like Next Action): never becomes a DC Note record — the note
  // round-trips as one dated line of the contact's plain-text Notes field
  // (the Contact-Notes bridge). Requires a contact link; the API 400s otherwise.
  { value: 'Contact Notes',          label: 'Contact Notes', description: 'Appends to the DealCloud contact’s Notes field — requires a linked contact.' },
  { value: 'Intro call with seller', label: 'Intro call with seller', description: 'KPI: logs a D/S call as held. Feeds the D/S-call dashboards on both CRMs.' },
  { value: 'Buyer Seller Call',      label: 'Buyer Seller Call', description: 'KPI: logs a B/S call as held.' },
  { value: 'Quarterly Call',         label: 'Quarterly Call' },
  { value: 'GPP Meeting',            label: 'GPP Meeting' },
  { value: 'Referral Partner Meeting', label: 'Referral Partner Meeting', description: 'KPI: logs a referral partner meeting.' },
  { value: 'Post-Closing Touchpoint',  label: 'Post-Closing Touchpoint' },
  { value: 'NDA Signed',             label: 'NDA Signed' },
  { value: 'Compass Equity',         label: 'Compass Equity' },
];
// The two KPI call subtypes above also stamp the company's call-milestone
// date field in DC ("Director/Seller Call Fields" → DirectorSellerCallDate,
// and the B/S equivalent) — see routes/notes.py. Composers show the call-date
// input whenever one of these is picked, even if the note type isn't Call.
const KPI_CALL_SUBTYPES = ['Intro call with seller', 'Buyer Seller Call'];
// DC also has Call/Fax/Appointment Note.Type choices — deliberately not
// offered: "Call" invites miscounts next to the two KPI call subtypes, and
// Fax/Appointment are legacy DC vocabulary.
const NOTE_TYPES = [
  { value: 'general',    label: 'General' },
  { value: 'call',       label: 'Call' },
  { value: 'meeting',    label: 'Meeting' },
  { value: 'email',      label: 'Email' },
  { value: 'memo',       label: 'Memo' },
  { value: 'internal',   label: 'Internal' },
  { value: 'ai_summary', label: 'AI summary' },
];

// Display date for a note card: call notes prefer the backdated call_date
// (when the call happened) over dc_created_at/created_at (when it was
// logged). call_date is a bare YYYY-MM-DD — parse it as local time, since
// new Date('YYYY-MM-DD') reads as UTC midnight and shows the previous day
// in timezones west of Greenwich.
const NoteCardDate = (n) => {
  if (n && n.note_type === 'call' && n.call_date) {
    const [y, m, d] = String(n.call_date).split('-').map(Number);
    if (y && m && d) return new Date(y, m - 1, d);
  }
  return new Date(n.dc_created_at || n.created_at);
};

// A Fireflies recording whose transcript has no sentences: the notetaker
// joined but nobody spoke, so the meeting probably didn't happen. A flag only
// — the note keeps its type and still counts.
const isPossibleNoShow = (n) => !!n && n.external_source === 'fireflies'
  && n.metadata && n.metadata.sentence_count != null && Number(n.metadata.sentence_count) === 0;

const NoShowPill = () => (
  <span title="Fireflies recorded no conversation"><Pill hue="amber" sz="sm">Possible no-show</Pill></span>
);

// Local YYYY-MM-DD — toISOString() would shift the day west of Greenwich
// (after ~8pm ET it's already tomorrow in UTC). No arg = today.
const localISODate = (d = new Date()) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const REMINDER_QUICK_PICKS = [
  { label: 'Tomorrow', days: 1 },
  { label: '3 days',   days: 3 },
  { label: '1 week',   days: 7 },
  { label: '2 weeks',  days: 14 },
  { label: '1 month',  days: 30 },
];

// ── FollowUpComposer ──────────────────────────────────────────────────────
// One-shot "remind me" task draft: seeded title + due-date quick picks.
// Creates a follow_up task assigned to the active user, linked to whatever
// entity ids the caller passes — the reminder then surfaces in the Tasks
// inbox on the due date. Shared by NoteModal, the contact/company page
// headers, and the calendar event drawer. Tasks have no note/event FK, so
// callers describe the source in `sourceLine` (rides in the task's notes).
const FollowUpComposer = ({ seedTitle, links = {}, sourceLine, linkedName, onClose, style }) => {
  const [title, setTitle] = React.useState(seedTitle || 'Follow up');
  const [dueDate, setDueDate] = React.useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 7);
    return localISODate(d);
  });
  const [saving, setSaving] = React.useState(false);

  const create = async () => {
    if (saving) return;
    const uid = (() => { try { return localStorage.getItem('atlas.activeUserId'); } catch (_) { return null; } })();
    if (!uid) { pushToast({ type: 'error', title: 'No active user', body: 'Pick a user in the header first.' }); return; }
    if (!title.trim() || !dueDate) {
      pushToast({ type: 'warning', title: 'Missing', body: 'Need a title and a due date.' });
      return;
    }
    setSaving(true);
    try {
      await window.AtlasApi.createTask({
        title: title.trim(),
        due_date: dueDate,
        task_type: 'follow_up',
        assigned_to: uid,
        created_by: uid,
        company_id: links.company_id || undefined,
        engagement_id: links.engagement_id || undefined,
        contact_id: links.contact_id || undefined,
        notes: sourceLine || undefined,
      });
      const [y, m, d] = dueDate.split('-').map(Number);
      pushToast({
        type: 'success', title: 'Reminder created',
        body: `Due ${new Date(y, m - 1, d).toLocaleDateString([], { month: 'short', day: 'numeric' })} — it'll show in your Inbox.`,
      });
      onClose();
    } catch (e) {
      pushToast({ type: 'error', title: 'Could not create reminder', body: e.message });
      setSaving(false);
    }
  };

  // Touch: finger-sized quick picks. Phone: the date gets its own full-width
  // row, the explanation sits above the buttons, and Create spans the card
  // (the iPhone is a ~340px column — nothing here may sit side by side).
  const d = dev();
  const P = !!d.phone;
  const pickH = d.touch ? 36 : null;
  const explain = (
    <span style={{ fontSize: P ? 13 : 11, color: 'var(--text-3)', lineHeight: P ? 1.4 : undefined }}>
      Lands in your Inbox on the due date{linkedName ? `, linked to ${linkedName}` : ''}.
    </span>
  );

  return (
    <div style={{ padding: 12, background: 'var(--accent-soft)',
                   borderRadius: 6, border: '1px solid var(--border)', ...style }}>
      <div style={{ fontSize: 10.5, color: 'var(--text-4)', textTransform: 'uppercase',
                     letterSpacing: '0.05em', fontWeight: 500, marginBottom: 6 }}>
        Follow-up reminder
      </div>
      <TextInput value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="Reminder title" />
      <div style={{ display: 'flex', alignItems: 'center', gap: pickH ? 8 : 6, marginTop: pickH ? 10 : 8, flexWrap: 'wrap' }}>
        {REMINDER_QUICK_PICKS.map((p) => {
          const due = new Date();
          due.setDate(due.getDate() + p.days);
          const iso = localISODate(due);
          const on = dueDate === iso;
          return (
            <button key={p.label} type={pickH ? 'button' : undefined} onClick={() => setDueDate(iso)}
              style={{
                padding: pickH ? '0 14px' : '3px 9px', borderRadius: 999, fontSize: pickH ? (P ? 14 : 13) : 11, cursor: 'pointer',
                ...(pickH ? { height: pickH } : null),
                border: `1px solid ${on ? 'var(--accent)' : 'var(--border)'}`,
                background: on ? 'var(--accent)' : 'var(--surface)',
                color: on ? '#fff' : 'var(--text-2)', fontWeight: on ? 600 : 400,
              }}>
              {p.label}
            </button>
          );
        })}
        <TextInput type="date" value={dueDate}
          onChange={(e) => setDueDate(e.target.value)}
          style={P ? { width: '100%', minHeight: 44 } : { width: 140, marginLeft: 'auto', ...(d.touch ? { minHeight: 36 } : null) }} />
      </div>
      {P ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 12 }}>
          {explain}
          <Btn size="md" variant="accent" onClick={create} disabled={saving}
            style={{ width: '100%', justifyContent: 'center', height: 44, marginTop: 2 }}>
            {saving ? 'Creating…' : 'Create reminder'}
          </Btn>
          <Btn size="md" variant="ghost" onClick={onClose} disabled={saving}
            style={{ width: '100%', justifyContent: 'center' }}>
            Cancel
          </Btn>
        </div>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10, ...(d.touch ? { flexWrap: 'wrap', rowGap: 6 } : null) }}>
          {explain}
          <div style={{ flex: 1 }} />
          <Btn size="xs" variant="ghost" onClick={onClose} disabled={saving}>Cancel</Btn>
          <Btn size="xs" variant="accent" onClick={create} disabled={saving}>
            {saving ? 'Creating…' : 'Create reminder'}
          </Btn>
        </div>
      )}
    </div>
  );
};

// Phone note reader helpers. The header names the kind of note; an attachment
// row shows a thumbnail for the photo types an iPhone produces.
const _PHONE_NOTE_KIND = {
  call: 'Call', meeting: 'Meeting', email: 'Email', memo: 'Memo',
  internal: 'Internal note', ai_summary: 'AI summary',
};
const _NOTE_IMG_EXT = new Set(['jpg', 'jpeg', 'png', 'gif', 'webp', 'heic', 'heif', 'bmp']);
const _fmtAttSize = (n) => (n == null ? '' : n < 1024 ? `${n} B`
  : n < 1024 * 1024 ? `${(n / 1024).toFixed(0)} KB` : `${(n / 1024 / 1024).toFixed(1)} MB`);

const NoteModal = ({ noteId, onClose, onChange }) => {
  const [note, setNote] = React.useState(null);   // null = loading
  const [error, setError] = React.useState('');
  const [editing, setEditing] = React.useState(false);
  const [draft, setDraft] = React.useState(null);
  const [saving, setSaving] = React.useState(false);
  // Follow-up task draft seeded from the note (the "Follow up" header button).
  const [reminderOpen, setReminderOpen] = React.useState(false);
  const panelRef = React.useRef(null);
  const titleId = React.useId();
  // Transcript state lives up here with the other hooks — every hook in this
  // component must run before the `if (!noteId) return null` bail-out below,
  // or opening a note changes the hook count between renders and React throws
  // "Rendered more hooks than during the previous render" (error #310), which
  // takes the whole page down. Sentence counts run into the hundreds, so the
  // transcript itself is still fetched lazily, only when the reader asks.
  const [transcript, setTranscript] = React.useState(null);
  const [tLoading, setTLoading]     = React.useState(false);
  const [tOpen, setTOpen]           = React.useState(false);
  // Phone: the note opens as a full-screen reader. Its header actions live
  // in a '…' action sheet, Follow up scrolls the reader back to the
  // composer, and "Add attachment" opens the photo/file picker directly.
  // (Same rule as above — these hooks run before the bail-out.)
  const device = window.useDevice ? window.useDevice() : dev();
  const P = !!device.phone;
  const [menuOpen, setMenuOpen] = React.useState(false);
  const [uploading, setUploading] = React.useState(false);
  const scrollRef = React.useRef(null);
  const fileRef = React.useRef(null);
  // Inside a workspace (LayerContext) the reader stacks above it, and goes
  // quiet while the workspace is hidden.
  const layer = React.useContext(LayerContext);
  const modalZ = _layerZ(60, layer.zBase);

  useFocusTrap(!!noteId && !layer.hidden, panelRef);
  useEscapeKey(!!noteId && !layer.hidden, onClose);

  // Load (and reload after edits).
  const reload = React.useCallback(() => {
    if (!noteId) return;
    setNote(null); setError(''); setReminderOpen(false); setMenuOpen(false);
    // The modal instance is reused across notes (only the noteId prop
    // changes), so drop the previous note's transcript with it.
    setTranscript(null); setTLoading(false); setTOpen(false);
    window.AtlasApi.getNote(noteId)
      .then((n) => { setNote(n); setDraft({
        title: n.title || '', body: n.body || '',
        note_type: n.note_type || 'general',
        dc_note_subtype: n.dc_note_subtype || '',
        is_pinned: !!n.is_pinned,
        tag_companies: (n.tagged_companies || []).map((c) => ({ value: c.id, label: c.name || 'Company' })),
        tag_contacts:  (n.tagged_contacts  || []).map((c) => ({ value: c.id, label: c.name || 'Contact' })),
      }); })
      .catch((e) => setError(e.message || 'Failed to load note'));
  }, [noteId]);
  React.useEffect(() => { reload(); }, [reload]);

  if (!noteId) return null;

  const isExternal = !!(note && note.external_source);
  const meta = (note && note.metadata) || {};
  const attendees = meta.attendees || [];
  const linkTrace = meta.link_trace || [];
  const aiInf    = meta.ai_inference || null;
  const transcriptUrl = meta.transcript_url || note?.external_url;
  const expectedSentences = meta.sentence_count || 0;

  const toggleTranscript = async () => {
    if (tOpen) { setTOpen(false); return; }
    setTOpen(true);
    if (transcript || tLoading) return;
    setTLoading(true);
    try {
      setTranscript(await window.api.noteTranscript(noteId));
    } catch (e) {
      setTranscript({ available: false, error: e.message || 'Failed to load transcript' });
    } finally {
      setTLoading(false);
    }
  };

  const save = async () => {
    setSaving(true); setError('');
    try {
      // Integration-sourced notes: only allow subtype + pin to be patched.
      // Title/body changes would race with the next sync.
      const patch = isExternal
        ? { dc_note_subtype: draft.dc_note_subtype || null, is_pinned: draft.is_pinned }
        : {
            title: draft.title || null,
            body:  draft.body,
            note_type: draft.note_type,
            dc_note_subtype: draft.dc_note_subtype || null,
            is_pinned: draft.is_pinned,
          };
      // Cross-filing tags are editable on everything except DC-imported
      // notes — there the sync owns them (mirrors of DC's ref arrays).
      if (note.source !== 'dealcloud_import') {
        patch.tag_company_ids = draft.tag_companies.map((c) => c.value);
        patch.tag_contact_ids = draft.tag_contacts.map((c) => c.value);
      }
      await window.AtlasApi.updateNote(noteId, patch);
      pushToast({ type: 'success', title: 'Saved' });
      setEditing(false);
      onChange && onChange();
      reload();
    } catch (e) {
      setError(e.message || 'Save failed');
      // Phone: the error banner sits at the top of a reader that may be
      // scrolled away from it — say it where it can't be missed.
      if (P) pushToast({ type: 'error', title: 'Save failed', body: e.message });
    } finally { setSaving(false); }
  };

  // Canonical link to this note: #/notes/<id> opens the Notes view with this
  // note's modal already up (router.jsx). Notes carry no per-note ACL — any
  // Atlas team member who opens the link sees it — so this is a share link
  // for colleagues, not a public one. The native shell runs at
  // capacitor://localhost, a dead link for whoever it's pasted to, so the
  // link uses the public web address (index.html) whenever it differs.
  const shareUrl = () => {
    const hash = window.AtlasRouter?.buildHash?.({ view: 'notes', noteId })
      || ('/notes/' + encodeURIComponent(noteId));
    const { origin, pathname, search } = window.location;
    const pub = window.__ATLAS_PUBLIC_URL__;
    if (pub && pub !== origin) return `${pub.replace(/\/+$/, '')}/#${hash}`;
    return `${origin}${pathname}${search}#${hash}`;
  };

  const copyLink = async () => {
    const url = shareUrl();
    try {
      await navigator.clipboard.writeText(url);
      pushToast({ type: 'success', title: 'Link copied',
                  body: 'Opens this note for anyone on the Atlas team.' });
      return;
    } catch (_) { /* fall through to the execCommand path */ }
    // navigator.clipboard needs a secure context and can be denied by
    // permissions policy; the old textarea + execCommand trick still works in
    // those cases. If even that fails, show the URL rather than fail silently.
    try {
      const ta = document.createElement('textarea');
      ta.value = url;
      ta.setAttribute('readonly', '');
      ta.style.cssText = 'position:fixed;top:-1000px;opacity:0';
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(ta);
      if (!ok) throw new Error('execCommand refused');
      pushToast({ type: 'success', title: 'Link copied',
                  body: 'Opens this note for anyone on the Atlas team.' });
    } catch (_) {
      pushToast({ type: 'warning', title: 'Copy this link', body: url });
    }
  };

  // Phone share sheet (Messages, Slack, Mail…). Cancelling it is not an
  // error; anything else falls back to copying the link.
  const shareLink = async () => {
    try {
      await navigator.share({ title: (note && note.title) || 'Atlas note', url: shareUrl() });
    } catch (e) {
      if (e && e.name === 'AbortError') return;
      copyLink();
    }
  };

  const onDelete = () => {
    showConfirm({
      title: 'Delete note?',
      body: 'This permanently removes the note. Attachments stay in storage.',
      onConfirm: async () => {
        try {
          await window.AtlasApi.deleteNote(noteId);
          pushToast({ type: 'success', title: 'Deleted' });
          onChange && onChange();
          onClose();
        } catch (e) {
          pushToast({ type: 'error', title: 'Delete failed', body: e.message });
        }
      },
    });
  };

  const detach = (att) => {
    showConfirm({
      title: 'Detach attachment?',
      body: `Remove "${att.original_filename}" from this note? Files linked to other notes (and DealCloud-synced files) stay in Files; a manual upload with no other links is deleted.`,
      onConfirm: async () => {
        try {
          await window.AtlasApi.detachAttachment(noteId, att.id);
          pushToast({ type: 'success', title: 'Detached' });
          onChange && onChange();
          reload();
        } catch (e) {
          pushToast({ type: 'error', title: 'Detach failed', body: e.message });
        }
      },
    });
  };

  // Phone "Add attachment" — the same upload call AttachButton makes. Camera
  // shots all arrive named image.jpg, so they get a timestamped name first.
  const uploadFiles = async (list) => {
    const files = Array.from(list || []).map((f, i) => _stampFileName(f, i, 'photo'));
    if (!files.length || uploading) return;
    setUploading(true);
    try {
      const created = await window.AtlasApi.uploadAttachments(noteId, files);
      pushToast({ type: 'success', title: `Uploaded ${created.length} file${created.length === 1 ? '' : 's'}` });
      onChange && onChange();
      reload();
    } catch (e) {
      pushToast({ type: 'error', title: 'Upload failed', body: e.message });
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  // Render helpers
  const dateStr = (s) => s ? new Date(s).toLocaleString([], {
    month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
  }) : '—';

  // ── Pieces shared by the desktop modal and the phone reader ─────────────
  const metaBits = (
    <>
      {note?.author_name && <><UserLink id={note.author_id} name={note.author_name} style={{ color: 'var(--text-2)', fontWeight: 500 }} /><span>·</span></>}
      <span>{note?.note_type === 'call' && note?.call_date
        ? NoteCardDate(note).toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' })
        : dateStr(note?.dc_created_at || note?.created_at)}</span>
      {note?.dc_note_subtype && note.dc_note_subtype !== 'Other' && (
        <Pill hue="violet" sz="sm">{note.dc_note_subtype}</Pill>
      )}
      {note?.note_type && (!note?.dc_note_subtype || note.dc_note_subtype === 'Other') && (
        <Pill hue="slate" sz="sm">{window.NoteTypeLabel ? window.NoteTypeLabel(note) : note.note_type}</Pill>
      )}
      {isExternal && (
        <Pill hue="teal" sz="sm">
          {window.IntegrationLogo && window.IntegrationLogo(note.external_source, 10)}
          &nbsp;{window.IntegrationSourceLabel ? window.IntegrationSourceLabel(note.external_source) : note.external_source}
        </Pill>
      )}
      {isPossibleNoShow(note) && <NoShowPill />}
      {note?.is_pinned && <Pill hue="amber" sz="sm">📌 pinned</Pill>}
    </>
  );

  const titleInput = (
    <TextInput value={draft?.title || ''} onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                placeholder="Title (optional)"
                style={{ fontSize: 15, fontWeight: 600 }} />
  );

  // Follow-up reminder composer — a one-shot task draft. Tasks have no note
  // FK, so the note reference rides in the task's notes text via sourceLine.
  const reminderEl = note && reminderOpen && (
    <FollowUpComposer
      seedTitle={`Follow up: ${note.title || note.company_name || note.contact_name || 'note'}`}
      links={{ company_id: note.company_id, contact_id: note.contact_id, engagement_id: note.engagement_id }}
      sourceLine={`From ${note.note_type === 'email' ? 'email' : 'note'} "${note.title || '(untitled)'}" (${NoteCardDate(note).toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' })})`}
      linkedName={note.company_name || note.contact_name}
      onClose={() => setReminderOpen(false)}
      style={P ? { marginTop: 14 } : { marginBottom: 14 }} />
  );

  // Body — markdown view / formatted edit
  const bodyEl = note && (editing && !isExternal ? (
    <NoteBodyEditor
      value={draft?.body || ''}
      onChange={(e) => setDraft({ ...draft, body: e.target.value })}
      rows={14}
    />
  ) : (
    <Markdown className={P ? 'md-reader' : undefined}
      style={P ? { fontSize: 15.5, lineHeight: 1.6, color: 'var(--text)' } : undefined}>{note.body || ''}</Markdown>
  ));

  // Tag editor (subtype + pin) — visible in edit mode for both external and
  // internal notes since these are local-only. One column on a phone.
  const tagEditorEl = note && editing && (
    <div style={{ marginTop: 14, padding: 12, background: 'var(--surface-2)',
                   borderRadius: 6, border: '1px solid var(--border)' }}>
      <div style={{ display: 'grid', gridTemplateColumns: isExternal || P ? '1fr' : '1fr 1fr', gap: 10 }}>
        {!isExternal && (
          <Field label="Type">
            <Combobox value={draft.note_type}
              onChange={(e) => setDraft({ ...draft, note_type: e.target.value })}
              options={NOTE_TYPES} />
          </Field>
        )}
        <Field label="Subtype">
          <Combobox value={draft.dc_note_subtype}
            onChange={(e) => setDraft({ ...draft, dc_note_subtype: e.target.value })}
            options={[{ value: '', label: '— none —' }, ...NOTE_SUBTYPES]} />
        </Field>
      </div>
      {note.source !== 'dealcloud_import' && (
        <div style={{ marginTop: 10 }}>
          <div style={{ fontSize: 10.5, color: 'var(--text-4)', textTransform: 'uppercase',
                         letterSpacing: '0.05em', fontWeight: 500, marginBottom: 4 }}>
            Also tagged
          </div>
          <NoteTagPicker
            companies={draft.tag_companies}
            contacts={draft.tag_contacts}
            onCompanies={(v) => setDraft({ ...draft, tag_companies: v })}
            onContacts={(v) => setDraft({ ...draft, tag_contacts: v })} />
        </div>
      )}
      <label style={P
        ? { display: 'flex', alignItems: 'center', gap: 10, marginTop: 8, minHeight: 44, fontSize: 15, color: 'var(--text)' }
        : { display: 'flex', alignItems: 'center', gap: 6, marginTop: 6, fontSize: 12, color: 'var(--text-2)' }}>
        <input type="checkbox" checked={draft.is_pinned}
                style={P ? { width: 20, height: 20 } : undefined}
                onChange={(e) => setDraft({ ...draft, is_pinned: e.target.checked })} />
        📌 Pin to top
      </label>
      {isExternal && (
        <div style={{ marginTop: 8, fontSize: P ? 13 : 11, color: 'var(--text-3)' }}>
          Title and body are managed by the upstream {note.external_source} sync. Subtype and pin are local-only.
        </div>
      )}
    </div>
  );

  // Integration metadata — attendees, AI guess, link trace. On a phone the
  // transcript flows inline (no scroller nested inside the reader's).
  const detailsEl = note && (attendees.length > 0 || aiInf || linkTrace.length > 0 || meta.host_email || meta.action_items) && (
    <div style={{ marginTop: 18, borderTop: '1px solid var(--border)', paddingTop: 14 }}>
      {meta.host_email && (
        <div style={{ fontSize: 11.5, color: 'var(--text-3)', marginBottom: 8 }}>
          <span style={{ color: 'var(--text-4)' }}>Host:</span> {meta.host_email}
          {meta.duration && <> · <span style={{ color: 'var(--text-4)' }}>Duration:</span> {Math.round(meta.duration)} min</>}
        </div>
      )}
      {attendees.length > 0 && (
        <div style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 10.5, color: 'var(--text-4)', textTransform: 'uppercase',
                         letterSpacing: '0.05em', fontWeight: 500, marginBottom: 4 }}>
            Attendees ({attendees.length})
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {attendees.map((a, i) => (
              <span key={i} role={a.company_id ? 'button' : undefined}
                    onClick={a.company_id ? () => { window.AtlasNav?.goToCompany(a.company_id); onClose(); } : undefined}
                    style={{ cursor: a.company_id ? 'pointer' : 'default' }}>
                <Pill hue={(a.match === 'email_direct' || a.match === 'email_alias') ? 'green' : a.match === 'domain' ? 'teal' : a.match === 'title_fuzzy' ? 'amber' : 'slate'} sz="sm">
                  {a.name || a.email || a.matched_token || '?'}
                  {a.company_name && <> · {a.company_name}</>}
                  {a.match && <> · {a.match.replace(/_/g, ' ')}</>}
                </Pill>
              </span>
            ))}
          </div>
        </div>
      )}
      {aiInf && aiInf.likely_target_company && (
        <div style={{ marginBottom: 10, padding: '8px 10px', background: 'var(--accent-soft)',
                        borderRadius: 5, fontSize: 11.5, color: 'var(--accent-text)' }}>
          <strong>🤖 Claude's guess:</strong> {aiInf.likely_target_company}
          {aiInf.confidence && <> <Pill hue="slate" sz="sm">{aiInf.confidence}</Pill></>}
          {aiInf.reason && <div style={{ marginTop: 4, fontStyle: 'italic', fontSize: 11 }}>{aiInf.reason}</div>}
        </div>
      )}
      {meta.action_items && (
        <div style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 10.5, color: 'var(--text-4)', textTransform: 'uppercase',
                         letterSpacing: '0.05em', fontWeight: 500, marginBottom: 4 }}>
            Action items
          </div>
          <Markdown>{typeof meta.action_items === 'string' ? meta.action_items : JSON.stringify(meta.action_items, null, 2)}</Markdown>
        </div>
      )}
      {expectedSentences > 0 && (
        <div style={{ marginBottom: 10 }}>
          <div
            role="button"
            onClick={toggleTranscript}
            style={P
              ? { cursor: 'pointer', fontSize: 13, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.04em',
                  fontWeight: 600, minHeight: 44, display: 'flex', alignItems: 'center', userSelect: 'none' }
              : { cursor: 'pointer', fontSize: 10.5, color: 'var(--text-4)',
                  textTransform: 'uppercase', letterSpacing: '0.05em',
                  fontWeight: 500, marginBottom: 4, userSelect: 'none' }}>
            {tOpen ? '▾' : '▸'} Transcript ({expectedSentences} lines)
          </div>
          {tOpen && tLoading && (
            <div style={{ fontSize: 11.5, color: 'var(--text-3)' }}>Loading…</div>
          )}
          {tOpen && !tLoading && transcript && !transcript.available && (
            <div style={{ fontSize: 11.5, color: 'var(--text-3)', padding: '8px 10px',
                          background: 'var(--surface-2)', borderRadius: 5 }}>
              {transcript.error
                ? transcript.error
                : 'Not stored in Atlas yet.'}
              {transcriptUrl && !transcript.error && (
                <> <a href={transcriptUrl} target="_blank" rel="noopener noreferrer"
                      style={{ color: 'var(--accent-text)' }}>Open in Fireflies ↗</a></>
              )}
            </div>
          )}
          {tOpen && !tLoading && transcript && transcript.available && (
            <div style={P
              ? { padding: '10px 12px', background: 'var(--surface-2)', borderRadius: 8, fontSize: 14 }
              : { maxHeight: 320, overflowY: 'auto', padding: '8px 10px',
                  background: 'var(--surface-2)', borderRadius: 5, fontSize: 11.5 }}>
              {(transcript.speakers || []).length > 0 && (
                <div style={{ marginBottom: 6, color: 'var(--text-3)' }}>
                  {transcript.speakers.join(' · ')}
                </div>
              )}
              {(transcript.sentences || []).map((sn, i) => (
                <div key={i} style={{ marginBottom: 5, lineHeight: 1.45 }}>
                  <span style={{ color: 'var(--text-4)', fontWeight: 500 }}>{sn.speaker}: </span>
                  {sn.start_time != null && transcriptUrl ? (
                    <a href={`${transcriptUrl}?t=${sn.start_time}`}
                       target="_blank" rel="noopener noreferrer"
                       style={{ color: 'inherit', textDecoration: 'none' }}
                       title="Play from here in Fireflies">
                      {sn.text}
                    </a>
                  ) : sn.text}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      {linkTrace.length > 0 && (
        <details style={{ fontSize: 11, color: 'var(--text-3)' }}>
          <summary style={{ cursor: 'pointer', color: 'var(--text-3)' }}>
            Link trace ({linkTrace.length} step{linkTrace.length === 1 ? '' : 's'})
          </summary>
          <pre style={{ margin: '6px 0 0', padding: 8, background: 'var(--surface-2)',
                         borderRadius: 5, fontSize: 10.5, overflow: 'auto' }}>
            {linkTrace.map((s) => JSON.stringify(s)).join('\n')}
          </pre>
        </details>
      )}
    </div>
  );

  if (P) {
    // ── Phone: full-screen reader ─────────────────────────────────────────
    // Header [Close | kind | …]; in edit mode [Cancel | Edit note | Save],
    // so the primary action never sits under the on-screen keyboard.
    // Linked records and attachments are full-width rows below the body.
    const canAct = !!note && !window.AtlasIsGuest;
    const kind = note ? (_PHONE_NOTE_KIND[note.note_type] || 'Note') : 'Note';
    const phoneTitle = editing ? (isExternal ? 'Edit tags' : 'Edit note') : kind;
    const srcLabel = note && note.external_source
      ? note.external_source.charAt(0).toUpperCase() + note.external_source.slice(1) : 'source';
    const tap = { WebkitTapHighlightColor: 'transparent', touchAction: 'manipulation' };
    const hdrBtn = {
      ...tap, height: 44, minWidth: 44, padding: '0 10px', border: 'none', background: 'transparent',
      cursor: 'pointer', fontSize: 16, fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center',
      justifyContent: 'center',
    };
    const tile = (bg, fg) => ({
      width: 36, height: 36, borderRadius: 9, display: 'inline-flex', alignItems: 'center',
      justifyContent: 'center', background: bg, color: fg,
    });
    const scrollTop = () => requestAnimationFrame(() => {
      try { scrollRef.current && scrollRef.current.scrollTo({ top: 0, behavior: 'smooth' }); } catch (_) {}
    });
    const PRow = window.PhoneRow;
    const PSection = window.PhoneSectionHeader;

    // What the note is about, in one quiet line under the title.
    const context = note ? [note.engagement_name, note.company_name, note.contact_name].filter(Boolean) : [];

    const links = note ? [
      note.engagement_name && note.engagement_id && { key: 'eng', icon: <Icon.Pipeline size={16} />, hue: 'violet', label: note.engagement_name, sub: 'Engagement', go: () => window.AtlasNav?.goToDeal(note.engagement_id) },
      note.company_name && note.company_id && { key: 'co', icon: <Icon.Building size={16} />, hue: 'teal', label: note.company_name, sub: 'Company', go: () => window.AtlasNav?.goToCompany(note.company_id) },
      note.contact_name && note.contact_id && { key: 'ct', icon: <Icon.User size={16} />, hue: 'slate', label: note.contact_name, sub: 'Contact', go: () => window.AtlasNav?.goToContact(note.contact_id) },
      ...(note.tagged_companies || []).map((c) => ({ key: `tco-${c.id}`, icon: <Icon.Building size={16} />, hue: 'teal', label: c.name || 'Company', sub: 'Tagged company', go: () => window.AtlasNav?.goToCompany(c.id) })),
      ...(note.tagged_contacts || []).map((c) => ({ key: `tct-${c.id}`, icon: <Icon.User size={16} />, hue: 'slate', label: c.name || 'Contact', sub: 'Tagged contact', go: () => window.AtlasNav?.goToContact(c.id) })),
    ].filter(Boolean) : [];

    const atts = (note && note.attachments) || [];
    const attRow = (att) => {
      const available = att.storage_status === 'available' && att.storage_uri;
      const ext = (att.file_extension || '').toLowerCase();
      const hue = (window.ATTACHMENT_EXT_HUE && window.ATTACHMENT_EXT_HUE[ext]) || 'slate';
      const h = HUE[hue] || HUE.slate;
      const size = _fmtAttSize(att.size_bytes);
      const lead = available && _NOTE_IMG_EXT.has(ext)
        ? <AuthThumb attachmentId={att.id} alt={att.original_filename}
            style={{ width: 40, height: 40, borderRadius: 8, objectFit: 'cover', display: 'block', background: 'var(--surface-2)' }} />
        : <span style={{ ...tile(available ? h.bg : 'var(--surface-2)', available ? h.fg : 'var(--text-4)'), width: 40, height: 40,
            fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{(att.file_extension || '·').slice(0, 4)}</span>;
      return (
        <div key={att.id} style={{ display: 'flex', alignItems: 'stretch', borderBottom: '1px solid var(--border)', background: 'var(--surface)' }}>
          <PRow leading={lead} title={att.original_filename}
            subtitle={available ? [size, 'Tap to open'].filter(Boolean).join(' · ') : 'Missing — referenced in DealCloud, not in the backup'}
            onClick={available ? () => window.AtlasApi.openAttachment(att.id, att.original_filename) : undefined}
            chevron={false} dim={!available}
            style={{ flex: 1, minWidth: 0, width: 'auto', borderBottom: 'none' }} />
          {canAct && (
            <button type="button" aria-label={`Detach ${att.original_filename}`} onClick={() => detach(att)}
              style={{ ...tap, width: 52, flexShrink: 0, border: 'none', background: 'transparent', color: 'var(--text-3)', fontSize: 22, cursor: 'pointer' }}>×</button>
          )}
        </div>
      );
    };

    const menuItems = canAct ? [
      { key: 'follow', label: 'Follow up', sub: 'Create a reminder task from this note', icon: <Icon.Clock size={18} />,
        onClick: () => { setMenuOpen(false); setReminderOpen(true); scrollTop(); } },
      { key: 'edit', label: isExternal ? 'Edit tags' : 'Edit note', sub: isExternal ? 'Subtype, pin and tags' : null,
        icon: <Icon.Edit size={18} />, onClick: () => { setMenuOpen(false); setEditing(true); scrollTop(); } },
      // .click() must run inside this tap for iOS to open the picker.
      { key: 'attach', label: uploading ? 'Uploading…' : 'Add attachment', sub: 'Take a photo, or pick a photo or file', icon: <Icon.Upload size={18} />,
        disabled: uploading, onClick: () => { setMenuOpen(false); if (fileRef.current) fileRef.current.click(); } },
      { key: 'copy', label: 'Copy link', sub: 'Opens this note for anyone on the Atlas team', icon: <Icon.Copy size={18} />,
        onClick: () => { setMenuOpen(false); copyLink(); } },
      typeof navigator !== 'undefined' && navigator.share && { key: 'share', label: 'Share link…', icon: <Icon.External size={18} />,
        onClick: () => { setMenuOpen(false); shareLink(); } },
    ] : [];

    // Portaled for the same reason as the phone Drawer sheet: opened from a
    // note card inside a page, a fixed panel must not be boxed in by it.
    return ReactDOM.createPortal(
      <div ref={panelRef} role="dialog" aria-modal="true" aria-labelledby={titleId} className="atlas-sheet"
        style={{
          position: 'fixed', inset: 0, zIndex: modalZ, display: layer.hidden ? 'none' : 'flex', flexDirection: 'column',
          background: 'var(--surface)', paddingLeft: 'var(--sal)', paddingRight: 'var(--sar)',
        }}>
        <header style={{ flexShrink: 0, paddingTop: 'var(--sat)', borderBottom: '1px solid var(--border)', background: 'var(--surface)' }}>
          <div style={{ height: 48, display: 'flex', alignItems: 'center', padding: '0 6px' }}>
            <div style={{ width: 88, display: 'flex', justifyContent: 'flex-start', flexShrink: 0 }}>
              {editing ? (
                <button type="button" onClick={() => { setEditing(false); reload(); }} disabled={saving}
                  style={{ ...hdrBtn, color: 'var(--text-2)' }}>Cancel</button>
              ) : (
                <button type="button" onClick={onClose} style={{ ...hdrBtn, color: 'var(--accent-text)' }}>Close</button>
              )}
            </div>
            <div style={{ flex: 1, minWidth: 0, textAlign: 'center', fontSize: 16, fontWeight: 600, color: 'var(--text)',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{phoneTitle}</div>
            <div style={{ width: 88, display: 'flex', justifyContent: 'flex-end', flexShrink: 0 }}>
              {editing ? (
                <button type="button" onClick={save} disabled={saving}
                  style={{ ...hdrBtn, color: 'var(--accent-text)', fontWeight: 600, opacity: saving ? 0.5 : 1 }}>
                  {saving ? 'Saving…' : 'Save'}
                </button>
              ) : canAct ? (
                <button type="button" aria-label="Note actions" onClick={() => setMenuOpen(true)}
                  style={{ ...hdrBtn, color: 'var(--text-2)' }}><Icon.More size={20} /></button>
              ) : null}
            </div>
          </div>
        </header>

        <div ref={scrollRef} style={{
          flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden', WebkitOverflowScrolling: 'touch',
          overscrollBehavior: 'contain', paddingBottom: 'calc(28px + max(var(--sab), var(--kb, 0px)))',
        }}>
          {note === null && !error && (
            <div style={{ padding: 16 }}><SkeletonRows rows={6} variant="generic" /></div>
          )}
          {error && (
            <div style={{ margin: '14px 16px 0', fontSize: 14, color: 'var(--rose-fg)', background: 'var(--rose-bg)',
                           padding: '10px 12px', borderRadius: 8 }}>
              {error}
              {!note && (
                <div style={{ marginTop: 10 }}>
                  <Btn size="sm" variant="outline" onClick={reload}>Try again</Btn>
                </div>
              )}
            </div>
          )}

          {note && (
            <>
              <div style={{ padding: '14px 16px 0' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--text-3)', flexWrap: 'wrap' }}>
                  {metaBits}
                </div>
                <h2 id={titleId} style={{ fontSize: 20, fontWeight: 600, color: 'var(--text)', lineHeight: 1.3,
                                           wordBreak: 'break-word', marginTop: 8 }}>
                  {editing && !isExternal
                    ? titleInput
                    : (note.title || <span style={{ color: 'var(--text-3)', fontStyle: 'italic', fontWeight: 500 }}>(untitled)</span>)}
                </h2>
                {!editing && context.length > 0 && (
                  <div style={{ fontSize: 14, color: 'var(--text-3)', marginTop: 4, lineHeight: 1.4 }}>{context.join(' · ')}</div>
                )}
                {reminderEl}
                <div style={{ marginTop: 14 }}>{bodyEl}</div>
                {tagEditorEl}
                {editing && !isExternal && (
                  <Btn size="md" variant="outline" onClick={onDelete} disabled={saving}
                    style={{ width: '100%', justifyContent: 'center', marginTop: 18, color: 'var(--rose-fg)' }}>
                    Delete note
                  </Btn>
                )}
              </div>

              {!editing && PRow && PSection && (links.length > 0 || transcriptUrl) && (
                <div style={{ marginTop: 10 }}>
                  <PSection title="Linked to" />
                  <div style={{ borderTop: '1px solid var(--border)' }}>
                    {links.map((l) => {
                      const h = HUE[l.hue] || HUE.slate;
                      return (
                        <PRow key={l.key} leading={<span style={tile(h.bg, h.fg)}>{l.icon}</span>}
                          title={l.label} subtitle={l.sub}
                          onClick={() => { l.go(); onClose(); }} />
                      );
                    })}
                    {transcriptUrl && (
                      <PRow leading={<span style={tile('var(--surface-2)', 'var(--text-3)')}><Icon.External size={16} /></span>}
                        title={`Open in ${srcLabel}`} subtitle="Opens outside Atlas" chevron={false}
                        onClick={() => window.open(transcriptUrl, '_blank', 'noopener,noreferrer')} />
                    )}
                  </div>
                </div>
              )}

              {!editing && detailsEl && <div style={{ padding: '0 16px' }}>{detailsEl}</div>}

              {!editing && PRow && PSection && (
                <div style={{ marginTop: 10 }}>
                  <PSection title="Attachments" count={atts.length || null} />
                  <div style={{ borderTop: '1px solid var(--border)' }}>
                    {atts.map(attRow)}
                    {canAct && (
                      <PRow leading={<span style={tile('var(--accent-soft)', 'var(--accent-text)')}><Icon.Plus size={16} /></span>}
                        title={uploading ? 'Uploading…' : 'Add photo or file'} chevron={false}
                        onClick={uploading ? undefined : () => { if (fileRef.current) fileRef.current.click(); }}
                        style={{ color: 'var(--accent-text)' }} />
                    )}
                    {!canAct && atts.length === 0 && (
                      <div style={{ padding: '12px 16px', fontSize: 14, color: 'var(--text-3)' }}>No attachments.</div>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {canAct && (
          <input ref={fileRef} type="file" multiple onChange={(e) => uploadFiles(e.target.files)} style={{ display: 'none' }} />
        )}
        {window.PhoneSheet && window.PhoneActionList && (
          <window.PhoneSheet open={menuOpen} onClose={() => setMenuOpen(false)} title={phoneTitle}>
            <window.PhoneActionList items={menuItems} />
          </window.PhoneSheet>
        )}
      </div>
      , document.body
    );
  }

  // iPad: the same centered modal, but the header actions wrap under the
  // title instead of squeezing it to a word per line in a narrow window, and
  // the card centers in the area above the on-screen keyboard (--kb) so the
  // footer Save stays reachable while the editor is focused.
  const wrapHeader = !!device.touch;

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: modalZ, display: layer.hidden ? 'none' : 'flex',
                   alignItems: 'center', justifyContent: 'center', padding: 20,
                   ...(wrapHeader ? { paddingBottom: 'calc(20px + var(--kb, 0px))' } : null) }}>
      <div onClick={onClose}
           style={{ position: 'absolute', inset: 0, background: 'oklch(0.2 0.01 270 / 0.32)' }} />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        style={{
          position: 'relative', width: 760, maxWidth: '100%', maxHeight: wrapHeader ? 'min(88vh, 100%)' : '88vh',
          background: 'var(--surface)', borderRadius: 'var(--radius-lg)',
          boxShadow: 'var(--shadow-lg)', border: '1px solid var(--border-strong)',
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
        }}
      >
        {/* Header */}
        <header style={{ padding: '14px 18px', borderBottom: '1px solid var(--border)',
                          display: 'flex', alignItems: 'flex-start', gap: 12,
                          ...(wrapHeader ? { flexWrap: 'wrap', rowGap: 8 } : null) }}>
          <div style={{ flex: 1, minWidth: wrapHeader ? 'min(100%, 320px)' : 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: 'var(--text-3)', flexWrap: 'wrap', marginBottom: 4 }}>
              {metaBits}
            </div>
            <h2 id={titleId} style={{ fontSize: 16, fontWeight: 600, color: 'var(--text)',
                                       lineHeight: 1.35, wordBreak: 'break-word' }}>
              {editing
                ? titleInput
                : (note?.title || <span style={{ color: 'var(--text-3)', fontStyle: 'italic' }}>(untitled)</span>)}
            </h2>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6,
                        ...(wrapHeader ? { flexWrap: 'wrap', marginLeft: 'auto' } : null) }}>
            {/* A deal guest reads notes only (backend app/deal_guest.py) —
                and a note link opens the Notes page, which they don't have. */}
            {note && !editing && !window.AtlasIsGuest && (
              <Btn size="xs" variant="ghost" icon={<Icon.Copy size={11} />}
                    title="Copy a link that opens this note"
                    onClick={copyLink}>
                Copy link
              </Btn>
            )}
            {note && !editing && !window.AtlasIsGuest && (
              <Btn size="xs" variant="outline" active={reminderOpen}
                    title="Create a follow-up reminder from this note"
                    onClick={() => setReminderOpen(!reminderOpen)}>
                ⏰ Follow up
              </Btn>
            )}
            {note && !editing && !window.AtlasIsGuest && (
              <Btn size="xs" variant="outline" onClick={() => setEditing(true)}>
                {isExternal ? 'Edit tags' : 'Edit'}
              </Btn>
            )}
            <button onClick={onClose} aria-label="Close" style={{
              width: wrapHeader ? 36 : 28, height: wrapHeader ? 36 : 28, borderRadius: 5, border: 'none',
              background: 'transparent', cursor: 'pointer', color: 'var(--text-3)',
              fontSize: wrapHeader ? 22 : 20, lineHeight: 1,
            }}>×</button>
          </div>
        </header>

        {/* Body */}
        <div style={{ flex: 1, overflow: 'auto', padding: '14px 18px' }}>
          {note === null && !error && (
            <div style={{ fontSize: 12, color: 'var(--text-3)' }}>Loading…</div>
          )}
          {error && (
            <div style={{ fontSize: 12, color: 'var(--rose-fg)', background: 'var(--rose-bg)',
                           padding: '8px 10px', borderRadius: 5 }}>{error}</div>
          )}

          {note && (
            <>
              {reminderEl}

              {/* Linked entities ─ click to navigate */}
              {(note.company_name || note.contact_name || note.engagement_name
                || (note.tagged_companies || []).length > 0 || (note.tagged_contacts || []).length > 0) && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 14 }}>
                  {note.company_name && note.company_id && (
                    <span role="button" style={{ cursor: 'pointer' }}
                          onClick={() => { window.AtlasNav?.goToCompany(note.company_id); onClose(); }}>
                      <Pill hue="teal" sz="sm">🏢 {note.company_name}</Pill>
                    </span>
                  )}
                  {note.contact_name && note.contact_id && (
                    <span role="button" style={{ cursor: 'pointer' }}
                          onClick={() => { window.AtlasNav?.goToContact(note.contact_id); onClose(); }}>
                      <Pill hue="slate" sz="sm">👤 {note.contact_name}</Pill>
                    </span>
                  )}
                  {note.engagement_name && note.engagement_id && (
                    <span role="button" style={{ cursor: 'pointer' }}
                          onClick={() => { window.AtlasNav?.goToDeal(note.engagement_id); onClose(); }}>
                      <Pill hue="violet" sz="sm">📁 {note.engagement_name}</Pill>
                    </span>
                  )}
                  {(note.tagged_companies || []).map((c) => (
                    <span key={`tco-${c.id}`} role="button" style={{ cursor: 'pointer' }}
                          onClick={() => { window.AtlasNav?.goToCompany(c.id); onClose(); }}>
                      <Pill hue="teal" sz="sm">🏢 {c.name || 'Company'}</Pill>
                    </span>
                  ))}
                  {(note.tagged_contacts || []).map((c) => (
                    <span key={`tct-${c.id}`} role="button" style={{ cursor: 'pointer' }}
                          onClick={() => { window.AtlasNav?.goToContact(c.id); onClose(); }}>
                      <Pill hue="slate" sz="sm">👤 {c.name || 'Contact'}</Pill>
                    </span>
                  ))}
                  {transcriptUrl && (
                    <a href={transcriptUrl} target="_blank" rel="noopener noreferrer"
                       style={{ marginLeft: 'auto', fontSize: 11.5, color: 'var(--accent-text)', textDecoration: 'none' }}>
                      Open in {note.external_source ? note.external_source.charAt(0).toUpperCase() + note.external_source.slice(1) : 'source'} ↗
                    </a>
                  )}
                </div>
              )}

              {bodyEl}

              {tagEditorEl}

              {detailsEl}

              {/* Attachments — the feed cards only show a 📎 count now, so the
                  modal owns attach/detach for existing notes. */}
              <div style={{ marginTop: 14, borderTop: '1px solid var(--border)', paddingTop: 12 }}>
                <div style={{ fontSize: 10.5, color: 'var(--text-4)', textTransform: 'uppercase',
                               letterSpacing: '0.05em', fontWeight: 500, marginBottom: 6 }}>
                  Attachments{note.attachments && note.attachments.length > 0 ? ` (${note.attachments.length})` : ''}
                </div>
                {window.AttachmentChipList && (
                  <window.AttachmentChipList
                    attachments={note.attachments}
                    onDetach={window.AtlasIsGuest ? undefined : detach}
                  />
                )}
                {window.AttachButton && !window.AtlasIsGuest && (
                  <div style={{ marginTop: 6 }}>
                    <window.AttachButton noteId={noteId}
                      onUploaded={() => { onChange && onChange(); reload(); }} />
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        {note && (
          <footer style={{ padding: '10px 16px', borderTop: '1px solid var(--border)',
                            display: 'flex', alignItems: 'center', gap: 8 }}>
            {editing && !isExternal && (
              <Btn size="xs" variant="ghost" onClick={onDelete}
                    style={{ color: 'var(--rose-fg)', marginRight: 'auto' }}>
                Delete
              </Btn>
            )}
            <div style={{ flex: 1 }} />
            {editing ? (
              <>
                <Btn size="xs" variant="ghost" onClick={() => { setEditing(false); reload(); }} disabled={saving}>Cancel</Btn>
                <Btn size="xs" variant="accent" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Btn>
              </>
            ) : (
              <Btn size="xs" variant="ghost" onClick={onClose}>Close</Btn>
            )}
          </footer>
        )}
      </div>
    </div>
  );
};

// `md-reader` (phone note reader, Ask Atlas answers): the reading-size
// variant. The base .md-body rules (index.html) pin headings to 12.5–14px for
// dense feed cards, which would read SMALLER than a 15px phone body — so the
// reader scales them (and code/tables, spacing) in em off its own size.
const __MD_READER_STYLE_ID = 'atlas-md-reader-styles';
const __ensureMdReaderStyles = () => {
  if (typeof document === 'undefined' || document.getElementById(__MD_READER_STYLE_ID)) return;
  const el = document.createElement('style');
  el.id = __MD_READER_STYLE_ID;
  el.textContent = `
    .md-body.md-reader h1 { font-size: 1.25em; } .md-body.md-reader h2 { font-size: 1.15em; }
    .md-body.md-reader h3 { font-size: 1.07em; } .md-body.md-reader h4 { font-size: 1em; }
    .md-body.md-reader h1, .md-body.md-reader h2, .md-body.md-reader h3, .md-body.md-reader h4 { margin: 14px 0 6px; }
    .md-body.md-reader p { margin: 0 0 10px; }
    .md-body.md-reader ul, .md-body.md-reader ol { margin: 4px 0 10px; padding-left: 22px; }
    .md-body.md-reader li { margin: 3px 0; }
    .md-body.md-reader code { font-size: 0.88em; }
    .md-body.md-reader table { font-size: 0.85em; }
    .md-body.md-reader th, .md-body.md-reader td { padding: 5px 9px; }`;
  document.head.appendChild(el);
};

// ── Markdown ───────────────────────────────────────────────────────────────
// Renders note bodies that arrive as markdown (Fireflies AI summaries use
// `**bold**`, `- bullets`, headings). Plain-text bodies pass through fine.
// Sanitized through DOMPurify because some upstream content (transcripts,
// participant-typed notes) could include hostile HTML — we don't want a
// meeting attendee's free-text field to execute a script.
//
// Both `marked` and `DOMPurify` are loaded as globals from index.html. If
// either fails to load, we degrade gracefully to plain pre-wrapped text.
//
// `strict` is for model-written text (CIM deliverables, section handoffs):
// raw HTML in the source shows as text, images are dropped, only https: and
// mailto: links stay links (new tab, noopener) and everything else is plain
// text, over a tight tag allowlist. Non-strict rendering is unchanged.
const __STRICT_MD_TAGS = [
  'p', 'br', 'strong', 'em', 'b', 'i', 'code', 'pre', 'blockquote', 'ul', 'ol', 'li',
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'table', 'thead', 'tbody', 'tr', 'th', 'td', 'hr', 'a', 'del',
];
const __escHtml = (t) => String(t == null ? '' : t)
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
// A marked renderer of its own, passed per parse — never marked.use(), which
// would change every other <Markdown>. Takes both renderer signatures
// (marked 12: html(text, block); 13+: html({ text, block })).
let __strictMdRenderer = null;
const __strictRenderer = () => {
  if (__strictMdRenderer) return __strictMdRenderer;
  if (typeof window.marked.Renderer !== 'function') return null;
  const r = new window.marked.Renderer();
  r.html = (a, b) => {
    const tok = a && typeof a === 'object' ? a : { text: a, block: b };
    const text = __escHtml(tok.text);
    return tok.block ? '<p>' + text.trim() + '</p>\n' : text;
  };
  r.image = () => '';
  __strictMdRenderer = r;
  return r;
};
const __strictMarkdownHtml = (src) => {
  const renderer = __strictRenderer();
  if (!renderer) return null;
  try {
    const raw = window.marked.parse(src, { gfm: true, breaks: true, renderer });
    const clean = window.DOMPurify.sanitize(raw, {
      ALLOWED_TAGS: __STRICT_MD_TAGS, ALLOWED_ATTR: ['href', 'title', 'align', 'start'],
      ALLOW_DATA_ATTR: false, ALLOW_ARIA_ATTR: false,
    });
    // Links: https: / mailto: open in a new tab; any other href (http:,
    // relative, javascript:, …) unwraps to its text. An inert <template>
    // parses without loading or running anything.
    const tpl = document.createElement('template');
    tpl.innerHTML = clean;
    Array.from(tpl.content.querySelectorAll('a')).forEach((a) => {
      const href = (a.getAttribute('href') || '').trim();
      if (/^https:\/\//i.test(href) || /^mailto:/i.test(href)) {
        a.setAttribute('href', href);
        a.setAttribute('target', '_blank');
        a.setAttribute('rel', 'noopener noreferrer');
      } else {
        a.replaceWith(...Array.from(a.childNodes));
      }
    });
    return tpl.innerHTML;
  } catch (_) {
    return null;
  }
};
const Markdown = ({ children, className, style, strict = false }) => {
  if (className && className.indexOf('md-reader') !== -1) __ensureMdReaderStyles();
  const html = React.useMemo(() => {
    const src = (children || '').toString();
    if (!src.trim()) return '';
    if (typeof window.marked === 'undefined' || typeof window.DOMPurify === 'undefined') {
      return null;  // signal: fall back to plain text
    }
    if (strict) return __strictMarkdownHtml(src);
    // External links in rendered notes open a new tab — clicking a URL in a
    // note body shouldn't navigate the CRM away. Registered once, applies to
    // every sanitize() call (all our DOMPurify usage is markdown bodies).
    if (!window.__mdLinksNewTab) {
      window.__mdLinksNewTab = true;
      window.DOMPurify.addHook('afterSanitizeAttributes', (node) => {
        if (node.tagName === 'A' && /^https?:/i.test(node.getAttribute('href') || '')) {
          node.setAttribute('target', '_blank');
          node.setAttribute('rel', 'noopener noreferrer');
        }
      });
    }
    try {
      // gfm=true gets us autolinks + tables; breaks=true treats a single
      // newline as <br> so Fireflies' line-broken bullet lists render right.
      const raw = window.marked.parse(src, { gfm: true, breaks: true });
      return window.DOMPurify.sanitize(raw, { USE_PROFILES: { html: true } });
    } catch (_) {
      return null;
    }
  }, [children, strict]);

  if (html === null) {
    return <div className={className} style={{ whiteSpace: 'pre-wrap', ...style }}>{children}</div>;
  }
  return (
    <div
      className={`md-body ${className || ''}`}
      style={style}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
};

// Clickable team-member name → their profile (window.AtlasNav.goToUser). Falls
// back to plain text when there's no id. `avatar` prepends an Avatar chip.
const UserLink = ({ id, name, color, avatar = false, size = 18, style }) => {
  const ok = id && name && name !== '—';
  const nameSpan = <span style={style}>{name || '—'}</span>;
  // Phone: positioned, so the name paints above its host's tap box (an EditableCell's
  // PhoneHit) and a tap on it still opens the profile, as on desktop.
  const lift = ok ? phoneHitHost() : null;
  if (avatar) {
    const handlers = ok ? {
      onClick: (e) => { e.stopPropagation(); window.AtlasNav?.goToUser?.(id, name); },
      title: `Open ${name}'s profile`,
      onMouseOver: (e) => { e.currentTarget.style.opacity = 0.7; },
      onMouseOut: (e) => { e.currentTarget.style.opacity = 1; },
    } : {};
    return (
      <span {...handlers} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, ...(ok ? { cursor: 'pointer' } : {}), ...lift }}>
        <Avatar name={name || '—'} size={size} color={color} />{nameSpan}
      </span>
    );
  }
  if (!ok) return nameSpan;
  return (
    <span onClick={(e) => { e.stopPropagation(); window.AtlasNav?.goToUser?.(id, name); }}
      title={`Open ${name}'s profile`}
      onMouseOver={(e) => { e.currentTarget.style.textDecoration = 'underline'; }}
      onMouseOut={(e) => { e.currentTarget.style.textDecoration = 'none'; }}
      style={{ cursor: 'pointer', ...lift, ...style }}>{name}</span>
  );
};

// ── NoteBodyEditor ──────────────────────────────────────────────────────────
// Markdown-flavored note composer shared by every notes panel + the NoteModal
// editor. A thin toolbar over a plain textarea: buttons/shortcuts (⌘B ⌘I ⌘K)
// wrap the selection in markdown, and pasting rich text (Outlook, Word, web
// pages) converts the clipboard's HTML flavor to markdown instead of dumping
// flattened plain text. Bodies already render through <Markdown>, so the
// stored value stays plain markdown — no schema or rendering changes.

// HTML → markdown for the paste handler. Sanitized first (same DOMPurify
// profile as <Markdown>) so we only ever walk inert markup. Deliberately
// small: bold/italic/strike, links, headings, lists, quotes, code, <br>/<p>
// blocks. Everything else (Outlook signature tables, images, style spans)
// degrades to its text content.
const htmlToMarkdown = (html) => {
  let clean = html;
  if (typeof window.DOMPurify !== 'undefined') {
    clean = window.DOMPurify.sanitize(html, { USE_PROFILES: { html: true } });
  }
  let doc;
  try { doc = new DOMParser().parseFromString(clean, 'text/html'); }
  catch (_) { return null; }

  const walk = (node, ctx = {}) => {
    if (node.nodeType === Node.TEXT_NODE) {
      return ctx.pre ? node.textContent : node.textContent.replace(/\s+/g, ' ');
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return '';
    const tag = node.tagName.toLowerCase();
    const inner = () => Array.from(node.childNodes).map((c) => walk(c, ctx)).join('');
    const innerPre = () => Array.from(node.childNodes).map((c) => walk(c, { ...ctx, pre: true })).join('');

    switch (tag) {
      case 'br': return '\n';
      case 'hr': return '\n\n---\n\n';
      case 'b': case 'strong': {
        const t = inner().trim();
        return t ? `**${t}**` : '';
      }
      case 'i': case 'em': {
        const t = inner().trim();
        return t ? `*${t}*` : '';
      }
      case 's': case 'strike': case 'del': {
        const t = inner().trim();
        return t ? `~~${t}~~` : '';
      }
      case 'code': {
        if (ctx.pre) return inner();
        const t = inner().trim();
        return t ? `\`${t}\`` : '';
      }
      case 'pre': {
        const t = innerPre().replace(/^\n+|\n+$/g, '');
        return t ? `\n\n\`\`\`\n${t}\n\`\`\`\n\n` : '';
      }
      case 'a': {
        const href = node.getAttribute('href') || '';
        const t = inner().trim() || href;
        if (!/^https?:/i.test(href)) return t;
        return t === href ? href : `[${t}](${href})`;
      }
      case 'img': return node.getAttribute('alt') || '';
      case 'h1': case 'h2': case 'h3': case 'h4': case 'h5': case 'h6': {
        const t = inner().trim();
        return t ? `\n\n${'#'.repeat(+tag[1])} ${t}\n\n` : '';
      }
      case 'blockquote': {
        const t = inner().trim();
        return t ? '\n\n' + t.split('\n').map((l) => `> ${l}`).join('\n') + '\n\n' : '';
      }
      case 'ul': case 'ol': {
        const ordered = tag === 'ol';
        const indent = '  '.repeat(ctx.listDepth || 0);
        let i = 0;
        const items = Array.from(node.children)
          .filter((c) => c.tagName && c.tagName.toLowerCase() === 'li')
          .map((li) => {
            i += 1;
            const t = walk(li, { ...ctx, listDepth: (ctx.listDepth || 0) + 1 })
              .replace(/^\n+|\n+$/g, '');
            return `${indent}${ordered ? `${i}. ` : '- '}${t}`;
          });
        return items.length ? `\n\n${items.join('\n')}\n\n` : '';
      }
      case 'li': return inner();
      case 'p': case 'div': case 'section': case 'article':
      case 'table': case 'tbody': case 'thead': case 'tr': {
        const t = inner();
        return t.trim() ? `\n\n${t.trim()}\n\n` : '';
      }
      case 'td': case 'th': {
        const t = inner().trim();
        return t ? `${t}  ` : '';
      }
      case 'style': case 'script': case 'head': case 'title': return '';
      default: return inner();
    }
  };

  const md = walk(doc.body)
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
  return md || null;
};

// Clipboard files — a pasted screenshot arrives on the clipboard as a file,
// not as text. Chromium/Safari put it on .items, Firefox on .files; take
// whichever carries it.
const _clipboardFiles = (dt) => {
  const out = [];
  if (dt.items) {
    Array.prototype.forEach.call(dt.items, (it) => {
      if (it.kind !== 'file') return;
      const f = it.getAsFile();
      if (f) out.push(f);
    });
  }
  if (!out.length && dt.files && dt.files.length) Array.prototype.push.apply(out, Array.from(dt.files));
  return out;
};

// Pasted screenshots are nameless or all called "image.png", so three pastes
// would land as three indistinguishable attachments. Stamp those with the
// paste time; a file copied out of Finder/Explorer keeps its real name.
const _PASTE_EXT = {
  'image/png': 'png', 'image/jpeg': 'jpg', 'image/gif': 'gif',
  'image/webp': 'webp', 'image/svg+xml': 'svg', 'image/tiff': 'tiff',
};
// `prefix` names the source: 'pasted' here, 'photo' for the phone note
// reader's picker, where every iPhone camera shot arrives as image.jpg.
const _stampFileName = (f, i, prefix) => {
  const generic = !f.name || /^image(\.[a-z0-9]+)?$/i.test(f.name);
  if (!generic) return f;
  const d = new Date();
  const p2 = (n) => String(n).padStart(2, '0');
  const stamp = `${d.getFullYear()}${p2(d.getMonth() + 1)}${p2(d.getDate())}-${p2(d.getHours())}${p2(d.getMinutes())}${p2(d.getSeconds())}`;
  const dot = (f.name || '').lastIndexOf('.');
  const ext = _PASTE_EXT[f.type] || (dot > 0 ? f.name.slice(dot + 1) : 'png');
  const name = `${prefix}-${stamp}${i > 0 ? `-${i + 1}` : ''}.${ext}`;
  // File() is constructible everywhere we support; fall back to the original
  // rather than dropping the paste if it ever isn't.
  try { return new File([f], name, { type: f.type, lastModified: f.lastModified }); }
  catch (_) { return f; }
};
const _namePastedFile = (f, i) => _stampFileName(f, i, 'pasted');

const _TB_BTN = {
  height: 22, minWidth: 22, padding: '0 5px', borderRadius: 4,
  border: 'none', background: 'transparent', cursor: 'pointer',
  fontSize: 11.5, color: 'var(--text-3)', lineHeight: '22px',
};

// Touch sizing for the toolbar: finger-sized buttons (the 22px desktop ones
// are unhittable on glass). Phone is a notch smaller so the row fits a
// 375pt screen on one line.
const _TB_BTN_TOUCH = { height: 38, minWidth: 38, padding: '0 8px', borderRadius: 7, fontSize: 15, lineHeight: '38px' };
const _TB_BTN_PHONE = { height: 36, minWidth: 36, padding: '0 6px', lineHeight: '36px' };

const NoteBodyEditor = ({ value, onChange, placeholder, rows = 5, minHeight, style, autoFocus, onAttachFiles }) => {
  const ref = React.useRef(null);
  const device = window.useDevice ? window.useDevice() : dev();
  const touch = !!device.touch;
  // Touch: the textarea grows with its content instead of offering a resize
  // grip iOS doesn't have (and a nested scroller inside a scrolling sheet).
  React.useLayoutEffect(() => {
    const ta = ref.current;
    if (!touch || !ta) return;
    ta.style.height = 'auto';
    // 0 = not laid out (inside a hidden/collapsed parent): leave the rows-
    // based height rather than pinning the box at 2px.
    const sh = ta.scrollHeight;
    ta.style.height = sh ? `${sh + 2}px` : '';
  }, [value, touch]);
  const emit = (next, selStart, selEnd) => {
    // Touch: re-focus inside the tap itself — iOS only raises the keyboard
    // for a focus() that happens during a user gesture, not in the rAF below.
    if (touch && ref.current && document.activeElement !== ref.current) {
      try { ref.current.focus({ preventScroll: true }); } catch (_) {}
    }
    onChange({ target: { value: next } });
    // Restore focus + selection after React re-renders the controlled value.
    requestAnimationFrame(() => {
      const ta = ref.current;
      if (!ta) return;
      ta.focus();
      if (selStart != null) ta.setSelectionRange(selStart, selEnd ?? selStart);
    });
  };

  // Wrap the selection in before/after; if it's already wrapped, unwrap.
  const wrap = (before, after = before) => {
    const ta = ref.current; if (!ta) return;
    const { selectionStart: s, selectionEnd: e } = ta;
    const v = ta.value;
    const sel = v.slice(s, e);
    if (v.slice(s - before.length, s) === before && v.slice(e, e + after.length) === after) {
      emit(v.slice(0, s - before.length) + sel + v.slice(e + after.length),
        s - before.length, e - before.length);
      return;
    }
    emit(v.slice(0, s) + before + sel + after + v.slice(e),
      s + before.length, e + before.length);
  };

  // Prefix every line the selection touches (bullet / numbered / quote);
  // if every touched line already has the prefix, strip it instead.
  const linePrefix = (prefixFor) => {
    const ta = ref.current; if (!ta) return;
    const v = ta.value;
    const s = v.lastIndexOf('\n', ta.selectionStart - 1) + 1;
    let e = v.indexOf('\n', ta.selectionEnd);
    if (e === -1) e = v.length;
    const lines = v.slice(s, e).split('\n');
    const strip = (l, i) => l.replace(/^(\s*)(- |\d+\. |> )/, '$1');
    const allPrefixed = lines.every((l, i) => l !== strip(l, i) || !l.trim());
    const next = lines.map((l, i) => {
      if (!l.trim()) return l;
      return allPrefixed ? strip(l, i) : prefixFor(i) + strip(l, i);
    }).join('\n');
    emit(v.slice(0, s) + next + v.slice(e), s, s + next.length);
  };

  const insertLink = () => {
    const ta = ref.current; if (!ta) return;
    const { selectionStart: s, selectionEnd: e } = ta;
    const v = ta.value;
    const sel = v.slice(s, e);
    if (/^https?:/i.test(sel.trim())) {
      // Selected a bare URL → turn it into [text](url) with "text" selected.
      const next = `${v.slice(0, s)}[text](${sel.trim()})${v.slice(e)}`;
      emit(next, s + 1, s + 5);
      return;
    }
    const label = sel || 'text';
    const next = `${v.slice(0, s)}[${label}](url)${v.slice(e)}`;
    const urlAt = s + 1 + label.length + 2;   // select the "url" placeholder
    emit(next, urlAt, urlAt + 3);
  };

  // Enter inside a list item continues the list (next bullet / number /
  // quote marker); Enter on an empty item strips its marker instead, which
  // is how you leave the list. Returns false to fall through to the
  // browser's plain newline.
  const continueList = () => {
    const ta = ref.current; if (!ta) return false;
    const { selectionStart: s, selectionEnd: end } = ta;
    if (s !== end) return false;
    const v = ta.value;
    const lineStart = v.lastIndexOf('\n', s - 1) + 1;
    let lineEnd = v.indexOf('\n', s);
    if (lineEnd === -1) lineEnd = v.length;
    const m = v.slice(lineStart, lineEnd).match(/^(\s*)((?:[-*]|\d+[.)]|>) )(.*)$/);
    if (!m) return false;
    const [, indent, marker, content] = m;
    if (!content.trim()) {
      emit(v.slice(0, lineStart) + v.slice(lineStart + indent.length + marker.length), lineStart);
      return true;
    }
    const num = marker.match(/^(\d+)([.)]) $/);
    const ins = '\n' + indent + (num ? `${+num[1] + 1}${num[2]} ` : marker);
    emit(v.slice(0, s) + ins + v.slice(end), s + ins.length);
    return true;
  };

  // Tab / Shift+Tab indent or outdent the list items the selection touches
  // (sub-bullets). Non-list lines keep the browser default so Tab still
  // moves focus everywhere else.
  const indentList = (outdent) => {
    const ta = ref.current; if (!ta) return false;
    const v = ta.value;
    const { selectionStart: selS, selectionEnd: selE } = ta;
    const s = v.lastIndexOf('\n', selS - 1) + 1;
    let e = v.indexOf('\n', selE);
    if (e === -1) e = v.length;
    const lines = v.slice(s, e).split('\n');
    const isItem = (l) => /^\s*(?:[-*]|\d+[.)]|>) /.test(l);
    if (!lines.some(isItem)) return false;
    const next = lines.map((l) => (isItem(l) ? (outdent ? l.replace(/^ {1,2}/, '') : '  ' + l) : l)).join('\n');
    // Fully-outdented already: still swallow Tab so focus doesn't jump.
    if (next !== lines.join('\n')) {
      if (selS === selE) {
        // Collapsed cursor (one line): keep it in place — a whole-line
        // selection here would make the next keystroke wipe the line.
        emit(v.slice(0, s) + next + v.slice(e), Math.max(s, selS + next.length - lines[0].length));
      } else {
        emit(v.slice(0, s) + next + v.slice(e), s, s + next.length);
      }
    }
    return true;
  };

  const onKeyDown = (e) => {
    if (e.isComposing) return;
    if (e.key === 'Enter' && !e.shiftKey && !e.metaKey && !e.ctrlKey && !e.altKey) {
      if (continueList()) e.preventDefault();
      return;
    }
    if (e.key === 'Tab' && !e.metaKey && !e.ctrlKey && !e.altKey) {
      if (indentList(e.shiftKey)) e.preventDefault();
      return;
    }
    if (!(e.metaKey || e.ctrlKey)) return;
    const k = e.key.toLowerCase();
    if (k === 'b') { e.preventDefault(); wrap('**'); }
    else if (k === 'i') { e.preventDefault(); wrap('*'); }
    else if (k === 'k') { e.preventDefault(); insertLink(); }
  };

  // Paste handling, in order: files (a screenshot) go to the note's
  // attachments; otherwise prefer the clipboard's HTML flavor converted to
  // markdown. Plain-text-only pastes fall through to the browser default.
  const onPaste = (e) => {
    const dt = e.clipboardData;
    if (!dt) return;
    const plain = (dt.getData('text/plain') || '').trim();
    // Cmd/Ctrl+V of an image attaches it, exactly like picking or dropping
    // it. Only when the clipboard carries no text: an Outlook body with an
    // inline image is a text paste and must keep pasting as text. Nothing is
    // preventDefault-ed unless there are real files, so ordinary pastes are
    // untouched.
    if (onAttachFiles && !plain) {
      const files = _clipboardFiles(dt).map(_namePastedFile);
      if (files.length) {
        e.preventDefault();
        onAttachFiles(files);
        return;
      }
    }
    const html = dt.getData('text/html');
    if (!html) return;
    const md = htmlToMarkdown(html);
    // Only intercept when the conversion actually preserved formatting the
    // plain flavor would lose — otherwise native paste behaves better.
    if (!md || md === plain) return;
    e.preventDefault();
    const ta = ref.current; if (!ta) return;
    const { selectionStart: s, selectionEnd: end } = ta;
    const v = ta.value;
    emit(v.slice(0, s) + md + v.slice(end), s + md.length);
  };

  // `name` is the touch label (no ⌘ shortcut — there's no keyboard to press
  // it on, and title= tooltips never show on touch anyway).
  const tbBtn = (label, title, onClick, styleExtra, name) => (touch ? (
    <button type="button" aria-label={name || title} tabIndex={-1}
      // Keep the textarea's focus + selection through the tap: pointerdown
      // for touch, mousedown for the compatibility event iOS still sends.
      onPointerDown={(e) => e.preventDefault()}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      style={{ ..._TB_BTN, ...styleExtra, ..._TB_BTN_TOUCH, ...(device.phone ? _TB_BTN_PHONE : null) }}>{label}</button>
  ) : (
    <button type="button" title={title} tabIndex={-1}
      onMouseDown={(e) => e.preventDefault()}   // keep textarea focus/selection
      onClick={onClick}
      onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--surface-2)'; e.currentTarget.style.color = 'var(--text)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--text-3)'; }}
      style={{ ..._TB_BTN, ...styleExtra }}>{label}</button>
  ));
  const sep = <span style={{ width: 1, height: touch ? 20 : 13, background: 'var(--border)', margin: device.phone ? '0 3px' : '0 4px' }} />;
  // Sub-bullets: desktop indents with Tab / Shift+Tab, but an iPhone keyboard
  // has no Tab key, so touch gets explicit buttons. They act on the list
  // items the selection touches, exactly like the keys.
  const indentBtns = touch && (
    <>
      {sep}
      {tbBtn('⇤', 'Outdent', () => indentList(true), { fontSize: 16 }, 'Outdent list item')}
      {tbBtn('⇥', 'Indent', () => indentList(false), { fontSize: 16 }, 'Indent list item')}
    </>
  );

  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: touch ? 8 : 6, background: 'var(--surface)', ...style }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: touch ? 2 : 1, padding: touch ? '3px 4px' : '3px 6px',
        borderBottom: '1px solid var(--border)', flexWrap: 'wrap' }}>
        {tbBtn(<b>B</b>, 'Bold (⌘B)', () => wrap('**'), undefined, 'Bold')}
        {tbBtn(<i>I</i>, 'Italic (⌘I)', () => wrap('*'), undefined, 'Italic')}
        {/* Strikethrough is the one a phone does without (room for indent). */}
        {!device.phone && tbBtn(<s>S</s>, 'Strikethrough', () => wrap('~~'))}
        {sep}
        {tbBtn('•', 'Bullet list', () => linePrefix(() => '- '))}
        {tbBtn('1.', 'Numbered list', () => linePrefix((i) => `${i + 1}. `))}
        {tbBtn('❝', 'Quote', () => linePrefix(() => '> '))}
        {indentBtns}
        {sep}
        {tbBtn('🔗', 'Link (⌘K)', insertLink, { fontSize: touch ? 14 : 10.5 }, 'Link')}
        {!touch && (
          <span style={{ marginLeft: 'auto', fontSize: 10, color: 'var(--text-4)', paddingRight: 2 }}>
            Markdown · rich text pastes keep formatting{onAttachFiles ? ' · paste a screenshot to attach it' : ''}
          </span>
        )}
      </div>
      <textarea
        ref={ref}
        value={value}
        onChange={onChange}
        onKeyDown={onKeyDown}
        onPaste={onPaste}
        placeholder={placeholder}
        rows={touch ? Math.max(rows, 6) : rows}
        autoFocus={autoFocus}
        style={touch
          ? { width: '100%', display: 'block', minHeight, padding: '10px 12px', fontSize: device.phone ? 16 : 14,
              border: 'none', outline: 'none', borderRadius: '0 0 8px 8px', background: 'transparent',
              color: 'var(--text)', fontFamily: 'inherit', lineHeight: 1.5, resize: 'none', overflow: 'hidden' }
          : { width: '100%', display: 'block', minHeight, padding: '8px 10px', fontSize: 12.5,
              border: 'none', outline: 'none', borderRadius: '0 0 6px 6px', background: 'transparent',
              color: 'var(--text)', fontFamily: 'inherit', lineHeight: 1.55, resize: 'vertical' }}
      />
    </div>
  );
};

// Shared option loader for server-backed company pickers (Combobox
// loadOptions). Empty query → a familiar my_team starter slice; typing →
// tokenized search across the full 312k-company mirror WITHOUT the my_team
// gate, since the company being linked is often unassigned or in the lead
// pool (the "Ritz Interiors invisible in New person" bug).
const searchCompanyOptions = (q) => (
  q ? window.AtlasApi.listCompanies({ q, limit: 20 })
    : window.AtlasApi.listCompanies({ my_team: true, limit: 50 })
).then((r) => (r.items || []).map((c) => ({ value: c.id, label: c.name })));

// Contact twin of searchCompanyOptions — company name rides along as an
// inline `hint` so same-named contacts stay tellable-apart without hovering.
const searchContactOptions = (q) => (
  q ? window.AtlasApi.listContacts({ q, limit: 20 })
    : window.AtlasApi.listContacts({ limit: 25 })
).then((r) => (r.items || []).map((c) => ({
  value: c.id,
  label: c.full_name || `${c.first_name || ''} ${c.last_name || ''}`.trim() || '(unnamed)',
  hint: c.company_name || undefined,
})));

// ── Note cross-filing (DealCloud-style multi-ref tags) ───────────────────
// A note lives on one primary record but can be TAGGED with more companies
// and contacts (tagged_companies / tagged_contacts on NoteRead) — "LOI
// signed" filed on the buyer, tagged with the seller, shows on both.

// Chips explaining where a note is filed/tagged. hideCompanyId /
// hideContactId suppress the page's own record (its feed implies it);
// what remains is the cross-filing worth showing. Click → navigate.
const NoteTagChips = ({ note, hideCompanyId, hideContactId, onNavigate }) => {
  const chips = [];
  const add = (key, hue, icon, label, go) => chips.push(
    <span key={key} role="button" style={{ cursor: 'pointer', ...phoneHitHost() }}
          onClick={(e) => { e.stopPropagation(); go(); onNavigate && onNavigate(); }}>
      <PhoneHit />
      <Pill hue={hue} sz="sm">{icon} {label}</Pill>
    </span>
  );
  if (note.company_id && note.company_name && note.company_id !== hideCompanyId)
    add(`co-${note.company_id}`, 'teal', '🏢', note.company_name,
        () => window.AtlasNav?.goToCompany(note.company_id));
  if (note.contact_id && note.contact_name && note.contact_id !== hideContactId)
    add(`ct-${note.contact_id}`, 'slate', '👤', note.contact_name,
        () => window.AtlasNav?.goToContact(note.contact_id));
  (note.tagged_companies || []).forEach((c) => {
    if (c.id !== hideCompanyId && c.id !== note.company_id)
      add(`tco-${c.id}`, 'teal', '🏢', c.name || 'Company',
          () => window.AtlasNav?.goToCompany(c.id));
  });
  (note.tagged_contacts || []).forEach((c) => {
    if (c.id !== hideContactId && c.id !== note.contact_id)
      add(`tct-${c.id}`, 'slate', '👤', c.name || 'Contact',
          () => window.AtlasNav?.goToContact(c.id));
  });
  if (!chips.length) return null;
  // Phone: each chip's 44px box reaches ~13px past it, so the row keeps that much room
  // above it and between wrapped rows, and 8px below (the note row's own 10px padding, or
  // a Next Action fold's 6px margin, makes up the rest): the note keeps its own taps.
  return <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4,
    ...(dev().phone ? { gap: '14px 6px', margin: '14px 0 8px' } : null) }}>{chips}</div>;
};

// Composer/editor widget for the tag sets. `companies`/`contacts` are
// [{value, label}]; the two add-boxes stay empty-valued so Combobox keeps
// showing the placeholder and each pick just appends a pill.
// Touch: a removable tag is a real button (~34px, the label says what a tap
// does); desktop keeps the compact pill with a hover title.
const NoteTagTouchChip = ({ hue, icon, label, onRemove }) => {
  const h = HUE[hue] || HUE.slate;
  return (
    <button type="button" onClick={onRemove} aria-label={`Remove tag ${label}`}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6, height: 34, maxWidth: '100%',
        padding: '0 6px 0 12px', borderRadius: 999, border: 'none', cursor: 'pointer',
        background: h.bg, color: h.fg, fontSize: 14, fontWeight: 500, fontFamily: 'inherit',
      }}>
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>{icon} {label}</span>
      <span aria-hidden="true" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 24, height: 24, borderRadius: 999, background: 'var(--surface)', color: 'var(--text-3)', fontSize: 12, flexShrink: 0 }}>✕</span>
    </button>
  );
};

const NoteTagPicker = ({ companies = [], contacts = [], onCompanies, onContacts }) => {
  const d = dev();
  return (
    <div>
      {(companies.length > 0 || contacts.length > 0) && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: d.touch ? 6 : 4, marginBottom: d.touch ? 8 : 6 }}>
          {companies.map((c) => (d.touch ? (
            <NoteTagTouchChip key={c.value} hue="teal" icon="🏢" label={c.label}
              onRemove={() => onCompanies(companies.filter((x) => x.value !== c.value))} />
          ) : (
            <span key={c.value} role="button" title="Remove tag" style={{ cursor: 'pointer' }}
                  onClick={() => onCompanies(companies.filter((x) => x.value !== c.value))}>
              <Pill hue="teal" sz="sm">🏢 {c.label} ✕</Pill>
            </span>
          )))}
          {contacts.map((c) => (d.touch ? (
            <NoteTagTouchChip key={c.value} hue="slate" icon="👤" label={c.label}
              onRemove={() => onContacts(contacts.filter((x) => x.value !== c.value))} />
          ) : (
            <span key={c.value} role="button" title="Remove tag" style={{ cursor: 'pointer' }}
                  onClick={() => onContacts(contacts.filter((x) => x.value !== c.value))}>
              <Pill hue="slate" sz="sm">👤 {c.label} ✕</Pill>
            </span>
          )))}
        </div>
      )}
      {/* Two side-by-side pickers don't fit a phone and squeeze in a narrow
          iPad pane — stack them there. */}
      <div style={{ display: 'grid', gridTemplateColumns: d.phone ? '1fr' : d.touch ? 'repeat(auto-fit, minmax(180px, 1fr))' : '1fr 1fr', gap: 8 }}>
        <Combobox value="" placeholder="+ Tag a company…" searchable options={[]}
          loadOptions={searchCompanyOptions}
          onChange={(e) => {
            if (e.target.value && !companies.some((x) => x.value === e.target.value))
              onCompanies([...companies, { value: e.target.value, label: e.label }]);
          }} />
        <Combobox value="" placeholder="+ Tag a contact…" searchable options={[]}
          loadOptions={searchContactOptions}
          onChange={(e) => {
            if (e.target.value && !contacts.some((x) => x.value === e.target.value))
              onContacts([...contacts, { value: e.target.value, label: e.label }]);
          }} />
      </div>
    </div>
  );
};

// AuthThumb — image preview for an attachment now that download URLs sit
// behind the login wall: a plain <img src> can't send the Authorization
// header, so resolve a short-lived signed URL through the authed API first.
// Fetches lazily (IntersectionObserver) so a long Files grid doesn't fire a
// hundred download-URL mints on mount, and the signed TTL starts counting
// only when the thumbnail actually scrolls into view.
const AuthThumb = ({ attachmentId, alt, style }) => {
  const [src, setSrc] = React.useState(null);
  const holderRef = React.useRef(null);
  React.useEffect(() => {
    let alive = true;
    const load = () => {
      window.AtlasApi.attachmentDownloadUrl(attachmentId)
        .then((r) => { if (alive && r && r.url) setSrc(r.url); })
        .catch(() => {});  // failed mint → keep the neutral placeholder
    };
    const el = holderRef.current;
    if (el && typeof IntersectionObserver !== 'undefined') {
      const io = new IntersectionObserver((entries) => {
        if (entries.some((e) => e.isIntersecting)) { io.disconnect(); load(); }
      }, { rootMargin: '200px' });
      io.observe(el);
      return () => { alive = false; io.disconnect(); };
    }
    load();
    return () => { alive = false; };
  }, [attachmentId]);
  if (!src) return <span ref={holderRef} style={{ display: 'block', background: 'var(--surface-2)', ...style }} />;
  return <img src={src} alt={alt} style={style} />;
};

// ── Task completion UX ───────────────────────────────────────────────────
// Shared by the Tasks page and the deal-page Tasks rail. Owns the whole
// "check off a task" interaction:
//   - optimistic checkmark (pop animation) the instant the box is clicked;
//   - the completed row lingers for a few seconds with inline "Done — next
//     <date>" feedback and an Undo affordance instead of vanishing on the
//     spot. Completing a RECURRING task spawns the next occurrence
//     server-side, and the old reload-immediately behavior made the clone
//     replace the row in place — it looked like the click did nothing.
//   - Undo (and unchecking from a Done list) re-opens the task; the server
//     retracts the spawned occurrence and reports it as retracted_next_due.
const __TASK_CHECK_STYLE_ID = 'atlas-task-check-keyframes';
const __ensureTaskCheckStyles = () => {
  if (document.getElementById(__TASK_CHECK_STYLE_ID)) return;
  const el = document.createElement('style');
  el.id = __TASK_CHECK_STYLE_ID;
  el.textContent = `
    @keyframes atlas-check-pop {
      0% { transform: scale(.4); }
      60% { transform: scale(1.25); }
      100% { transform: scale(1); }
    }`;
  document.head.appendChild(el);
};

const TASK_DONE_LINGER_MS = 5000;

// "2026-08-19" → "Wed, Aug 19" for the inline next-occurrence feedback.
const fmtTaskDue = (iso) => {
  const d = new Date(`${iso}T00:00:00`);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
};

const useTaskToggle = (onSettled) => {
  // task id → { phase: 'saving' | 'done', spawnedDue }. Presence means the
  // row should render checked; phase 'done' means show the linger feedback.
  const [pending, setPending] = React.useState({});
  const timers = React.useRef({});
  React.useEffect(() => {
    __ensureTaskCheckStyles();
    const held = timers.current;
    return () => Object.values(held).forEach(clearTimeout);
  }, []);

  const settle = (id) => {
    clearTimeout(timers.current[id]);
    delete timers.current[id];
    setPending((p) => {
      const next = { ...p };
      delete next[id];
      return next;
    });
  };

  const complete = async (t) => {
    if (pending[t.id]) return;  // in flight or lingering — ignore re-clicks
    setPending((p) => ({ ...p, [t.id]: { phase: 'saving' } }));
    let res;
    try {
      res = await window.AtlasApi.updateTask(t.id, { status: 'completed' });
    } catch (e) {
      settle(t.id);
      pushToast({ type: 'error', title: 'Could not complete task', body: e.message });
      return;
    }
    setPending((p) => ({ ...p, [t.id]: { phase: 'done', spawnedDue: (res && res.spawned_next_due) || null } }));
    timers.current[t.id] = setTimeout(() => {
      settle(t.id);
      if (onSettled) onSettled();
    }, TASK_DONE_LINGER_MS);
  };

  // Undo during the linger window, or unchecking from a Done list. Either
  // way the server flips completed → open and retracts the spawned next
  // occurrence (if it's still open and untouched by a later completion).
  const reopen = async (t) => {
    const lingering = !!pending[t.id];
    if (lingering && pending[t.id].phase === 'saving') return;
    clearTimeout(timers.current[t.id]);
    delete timers.current[t.id];
    if (lingering) setPending((p) => ({ ...p, [t.id]: { phase: 'saving' } }));
    try {
      const res = await window.AtlasApi.updateTask(t.id, { status: 'open' });
      if (!lingering && res && res.retracted_next_due) {
        pushToast({
          type: 'info', title: 'Task reopened',
          body: `Removed the auto-scheduled next occurrence (${fmtTaskDue(res.retracted_next_due)}).`,
        });
      }
    } catch (e) {
      pushToast({ type: 'error', title: 'Could not reopen task', body: e.message });
    }
    settle(t.id);
    if (onSettled) onSettled();
  };

  const toggle = (t) => (t.status === 'completed' ? reopen(t) : complete(t));
  const stateFor = (id) => pending[id] || null;
  return { toggle, undo: reopen, stateFor };
};


// ── Owner scope prompt — "one owner per book" (202609181200) ─────────────
// Changing the owner of a deal / contact / company asks how far it reaches:
// everything at the company (the default — a contact must never show a
// different owner than its company or the deal) or only this record, which
// marks it as individually assigned. Resolves 'all' | 'record' | null
// (cancelled). Same imperative host pattern as ConfirmHost.
let __ownerScopeHandler = null;
const showOwnerScope = ({ kind, ownerName, companyName }) =>
  new Promise((resolve) => {
    if (!__ownerScopeHandler) { resolve('all'); return; }
    __ownerScopeHandler({ kind, ownerName, companyName, resolve });
  });

const OWNER_SCOPE_COPY = {
  engagement: { only: 'Only this deal',    onlyBody: 'The deal is marked as individually assigned and stops following the company owner.' },
  contact:    { only: 'Only this contact', onlyBody: 'The contact is marked as individually assigned and stops following the company owner.' },
  company:    { only: 'Only the company',  onlyBody: 'Contacts and deals someone assigned individually keep their owner. Everything else at the company follows.' },
};

const OwnerScopeHost = () => {
  const [state, setState] = React.useState(null);
  const [scope, setScope] = React.useState('all');
  const dialogRef = React.useRef(null);
  const titleId = React.useId();
  React.useEffect(() => {
    __ownerScopeHandler = (s) => { setScope('all'); setState(s); };
    return () => { __ownerScopeHandler = null; };
  }, []);
  const finish = React.useCallback((v) => {
    setState((s) => { if (s) s.resolve(v); return null; });
  }, []);
  useFocusTrap(!!state, dialogRef);
  useEscapeKey(!!state, () => finish(null));
  if (!state) return null;
  const copy = OWNER_SCOPE_COPY[state.kind] || OWNER_SCOPE_COPY.engagement;
  const owner = state.ownerName || 'Unassigned';
  const company = state.companyName || 'this company';
  const Option = ({ value, title, body }) => {
    const on = scope === value;
    return (
      <button type="button" role="radio" aria-checked={on} onClick={() => setScope(value)} style={{
        display: 'flex', gap: 10, alignItems: 'flex-start', textAlign: 'left', width: '100%',
        padding: '10px 12px', borderRadius: 8, cursor: 'pointer', color: 'var(--text)',
        background: on ? 'var(--accent-soft)' : 'var(--surface)',
        border: `1px solid ${on ? 'var(--accent)' : 'var(--border)'}`,
      }}>
        <span aria-hidden style={{
          width: 14, height: 14, borderRadius: '50%', marginTop: 2, flexShrink: 0,
          border: `1.5px solid ${on ? 'var(--accent)' : 'var(--border-strong)'}`,
          background: on ? 'var(--accent)' : 'transparent',
          boxShadow: on ? 'inset 0 0 0 3px var(--surface)' : 'none',
        }} />
        <span style={{ minWidth: 0 }}>
          <span style={{ display: 'block', fontSize: 13, fontWeight: 600 }}>{title}</span>
          <span style={{ display: 'block', fontSize: 12, color: 'var(--text-2)', marginTop: 2, lineHeight: 1.4 }}>{body}</span>
        </span>
      </button>
    );
  };
  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 300,
      background: 'oklch(0.2 0.01 270 / 0.28)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}
      onClick={() => finish(null)}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--surface)', border: '1px solid var(--border-strong)',
          borderRadius: 10, boxShadow: 'var(--shadow-lg)',
          width: 420, maxWidth: '92vw', padding: '16px 18px',
        }}
      >
        <h3 id={titleId} style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>Change owner to {owner}</h3>
        <div style={{ fontSize: 12.5, color: 'var(--text-2)', marginTop: 6, lineHeight: 1.45 }}>Where should the new owner apply?</div>
        <div role="radiogroup" style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 12 }}>
          <Option value="all" title={`Everything at ${company}`} body="The company, all its contacts and every live deal — one owner for the whole book." />
          <Option value="record" title={copy.only} body={copy.onlyBody} />
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 14 }}>
          <Btn size="sm" variant="outline" onClick={() => finish(null)}>Cancel</Btn>
          <Btn size="sm" variant="accent" onClick={() => finish(scope)}>Apply</Btn>
        </div>
      </div>
    </div>
  );
};

// Save an owner pick through the scope prompt. `save(patch)` is the page's
// own PATCH helper and returns the updated record (so the cascade can be
// reported); a cancelled prompt throws so EditableCell reverts its draft.
// No company to cascade through, or clearing to Unassigned → no prompt,
// the record alone changes (and is marked individually assigned).
const saveOwnerWithScope = async ({ kind, field, value, users, companyName, hasCompany = true, save }) => {
  const u = (users || []).find((x) => x.id === value);
  const ownerName = value ? (u ? u.full_name : 'the selected user') : 'Unassigned';
  let scope = 'record';
  if (hasCompany && value) {
    scope = await showOwnerScope({ kind, ownerName, companyName });
    if (!scope) throw new Error('cancelled');
  }
  const updated = await save({ [field]: value, owner_scope: scope });
  const c = updated && updated.owner_cascade;
  if (c && scope === 'all') {
    const parts = [];
    if (c.companies)   parts.push(`${c.companies} company`);
    if (c.contacts)    parts.push(`${c.contacts} contact${c.contacts === 1 ? '' : 's'}`);
    if (c.engagements) parts.push(`${c.engagements} deal${c.engagements === 1 ? '' : 's'}`);
    if (parts.length) {
      pushToast({ type: 'success', title: `Owner → ${ownerName}`, body: `Also updated ${parts.join(', ')}.` });
    }
    window.dispatchEvent(new CustomEvent('atlas-data-changed'));
  }
  return updated;
};

// Small lock next to an owner cell whose record was assigned individually
// (owner_locked): it does not follow the company / industry owner until the
// owner is changed again with "Everything". Icon-only so a narrow property
// row still has room for the name; the title carries the explanation.
const OwnerLockHint = ({ kind = 'record' }) => {
  const text = kind === 'company'
    ? 'Owner set by hand — this company does not follow its industry lead. Change the owner to reset.'
    : 'Assigned individually (or set before the one-owner rule) — does not follow the company owner. Change the owner and pick "Everything" to make it follow again.';
  return (
    <span title={text} aria-label={text} role="img"
      style={{ display: 'inline-flex', alignItems: 'center', color: 'var(--text-4)', flexShrink: 0, cursor: 'help' }}>
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <rect x="5" y="11" width="14" height="10" rx="2" />
        <path d="M8 11V7a4 4 0 0 1 8 0v4" />
      </svg>
    </span>
  );
};

// A record's ID — the number on every record page, in links (#/id/<n>) and in
// imports / exports. It is the number DealCloud uses too (dealcloud_id),
// unique across companies, deals and contacts. Click copies it.
const RecordId = ({ value }) => {
  if (!value) return null;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(String(value));
      pushToast({ type: 'success', title: `ID ${value} copied` });
    } catch (_) {
      pushToast({ type: 'info', title: `ID ${value}` });
    }
  };
  const CopyIcon = window.Icon && window.Icon.Copy;
  return (
    <button type="button" onClick={copy} title="Copy ID" style={{
      background: 'transparent', border: 'none', padding: 0, cursor: 'pointer',
      display: 'inline-flex', alignItems: 'center', gap: 5, color: 'var(--text-3)', ...phoneHitHost(),
    }}>
      <PhoneHit />
      <code className="mono" style={{ fontSize: 10.5, color: 'var(--text-2)' }}>{value}</code>
      {CopyIcon && <CopyIcon size={10} />}
    </button>
  );
};

// A small "?" that explains a number or a rule on demand. Hover shows the
// note, click pins it (touch has no hover); Escape, a scroll, or a click
// elsewhere closes it. Explanations live here, never as body text under
// the heading (see docs/removed_ui_copy_2026-10-06.md for the history).
const HelpTip = ({ text, title, size = 13, width = 300, style }) => {
  const [open, setOpen] = React.useState(false);
  const [pinned, setPinned] = React.useState(false);
  const [pos, setPos] = React.useState(null);
  const anchorRef = React.useRef(null);
  const boxRef = React.useRef(null);
  const leaveT = React.useRef(null);
  const close = React.useCallback(() => { setOpen(false); setPinned(false); }, []);
  React.useLayoutEffect(() => {
    if (!open || !anchorRef.current) return;
    const r = anchorRef.current.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const left = Math.max(8, Math.min(r.left - 10, vw - width - 8));
    const below = r.bottom + 6;
    setPos(below + 160 > vh ? { left, bottom: vh - r.top + 6 } : { left, top: below });
  }, [open, width]);
  React.useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (boxRef.current && boxRef.current.contains(e.target)) return;
      if (anchorRef.current && anchorRef.current.contains(e.target)) return;
      close();
    };
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    const onScroll = (e) => { if (!(boxRef.current && boxRef.current.contains(e.target))) close(); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    document.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('scroll', onScroll, true);
    };
  }, [open, close]);
  React.useEffect(() => () => clearTimeout(leaveT.current), []);
  const enter = () => { clearTimeout(leaveT.current); setOpen(true); };
  const leave = () => { if (pinned) return; leaveT.current = setTimeout(() => setOpen(false), 150); };
  const Help = window.Icon && window.Icon.Help;
  return (
    <>
      <button type="button" ref={anchorRef} aria-label={title || 'What is this?'} aria-expanded={open}
        onMouseEnter={enter} onMouseLeave={leave} onFocus={enter} onBlur={leave}
        onClick={(e) => { e.stopPropagation(); if (pinned) close(); else { setPinned(true); setOpen(true); } }}
        style={{
          appearance: 'none', background: 'transparent', border: 'none', padding: 0, margin: '0 0 0 4px',
          cursor: 'help', display: 'inline-flex', alignItems: 'center', verticalAlign: 'middle', lineHeight: 0,
          color: open ? 'var(--text-2)' : 'var(--text-4)', ...style,
        }}>
        {Help ? <Help size={size} color="currentColor" /> : <span style={{ fontSize: size - 2, lineHeight: 1 }}>?</span>}
      </button>
      {open && pos && ReactDOM.createPortal(
        <div ref={boxRef} role="tooltip" data-combobox-menu=""
          onMouseEnter={enter} onMouseLeave={leave} onMouseDown={(e) => e.stopPropagation()}
          style={{
            position: 'fixed', ...pos, zIndex: 2000, width, maxWidth: 'calc(100vw - 16px)', boxSizing: 'border-box',
            padding: '9px 12px', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8,
            boxShadow: 'var(--shadow-lg)', fontSize: 12, lineHeight: 1.5, color: 'var(--text-2)', fontWeight: 400,
            textTransform: 'none', letterSpacing: 0, whiteSpace: 'normal', textAlign: 'left',
          }}>
          {title && <div style={{ fontWeight: 600, color: 'var(--text)', marginBottom: 3 }}>{title}</div>}
          {text}
        </div>,
        document.body,
      )}
    </>
  );
};

Object.assign(window, {
  animateEl, MOTION,
  Pill, Btn, PhoneHit, phoneHitHost, PhoneHitScope, Kbd, Avatar, UserLink, Logo, Sparkline, HealthBar, Section, LazyFold, Drawer, Field, TextInput,
  MoneyInput,
  Select, Combobox, setAllSections, headerCompact, searchCompanyOptions, searchContactOptions, pushToast, dismissToast, ToastHost, showConfirm, ConfirmHost,
  showOwnerScope, OwnerScopeHost, saveOwnerWithScope, OwnerLockHint,
  useFocusTrap, useEscapeKey, Markdown, NoteModal, Skeleton, SkeletonRow, SkeletonRows,
  NOTE_SUBTYPES, NOTE_TYPES, KPI_CALL_SUBTYPES, ROLE_META, RoleChips, NoteBodyEditor, htmlToMarkdown,
  NoteCardDate, isPossibleNoShow, NoShowPill, NoteTagChips, NoteTagPicker, AuthThumb, useTaskToggle, fmtTaskDue,
  FollowUpComposer, RecordId, localISODate, HelpTip, REMINDER_QUICK_PICKS,
});
